import { api, ApiError, cancelPendingReads, postSse } from './lib/api.js';
import { deleteRecording, localBlob } from './lib/idb.js';
import { annunciaVoce, finishRecording, Recorder } from './lib/recorder.js';
import { playUrl, speakLocal, stopSpeaking, unlockAudio } from './lib/tts.js';
import { clear, el, fmtDate, fmtTimer, quandoRelativo } from './lib/ui.js';
import { buildCallScreen } from './lib/call.js';
import { taccuino } from './lib/appunti.js';
import { editorRitaglio } from './lib/ritaglio.js';
import { cartaDisegno } from './lib/disegno.js';
import { cartaConsiglio } from './lib/consigli.js';
import { cartaNotizia, type NotiziaDaVedere } from './lib/notizia.js';
import { cartaRispostaOspite } from './lib/risposte-ospite.js';
import { fermaVideo, pausaVideo, rigaVideo, scorriAlVideo, sezioneVideo, videoDellaStoria, type RigaMedia, type VideoElenco } from './lib/video.js';
import { caricatore, type Regole } from './lib/video-upload.js';
import { homeShortcuts, notebookMenu, primaryNavigation as VOCI_BARRA } from './lib/navigation.js';
import { renderArchive, renderSearch } from './views/archive.js';
import { annuncioDaMostrare, renderQuaderno, segnaAnnuncio } from './views/quaderno.js';
import { renderAlbum } from './views/album.js';
import { renderPosta } from './views/posta.js';
import { renderHeartPlaces } from './views/heart-places.js';
import { filoUI, soloEmoji, type Novita, type Risposta } from './lib/filo-ui.js';
import { icon } from './lib/icons.js';
import { pageEditor, type PageStatus } from './lib/page-editor.js';
import { renderPersonEditor, renderRemovedPeople } from './views/person-editor.js';
import { renderFamilyTree } from './views/family-tree.js';
import { renderFamilyIdentity } from './views/family-identity.js';
import { clearSurprises, mountSurprises } from './lib/surprises.js';
import { appInstallata, attivaPush, spegniPush, statoPush, supportoPush, type PushEndpoints } from './lib/push.js';
import type { BookDedication } from '../shared/book.js';
import { annoIncompleto, annoIniziale, chiaveData, nellaData } from '../shared/date.js';

type TipoDomanda = 'vincenzo' | 'albero' | 'suo' | 'storia' | 'tema';
type TodayQuestion = { id: number; text: string; fromSon: boolean; ricetta?: boolean; voiceUrl: string | null; swapped: boolean; tipo?: TipoDomanda };
type HomeState = {
  name: string;
  caroName?: string;
  appBuild?: string;
  date: string;
  todayQuestion: TodayQuestion | null;
  /** «Di cosa ti va di parlare oggi?»: fino a tre domande; null = la carta di una domanda sola. */
  todayQuestions?: TodayQuestion[] | null;
  heartPlaces?: { title: string; sender: string } | null;
  questionsFromCaro?: TodayQuestion[];
  book: { memories: number; chapters: number };
  counts: { spunti: number; foto: number; persone: number; voci: number; verifiche: number };
  accadde: Array<{ kind: string; anno?: number; title?: string | null; storyId?: number; name?: string; personId?: number; anni?: number | null }>;
  baule: { id: number; title: string | null; anno: number | null; data?: string | null; photoId: number | null } | null;
  vociNuove: Array<{ id: number; narrator: string; title: string | null }>;
  home: { domanda: boolean; accadde: boolean; baule: boolean };
  reactions: Array<{ id: number; kind: string; text: string | null; media_id: number | null; memory_id: number; title: string | null }>;
  novita?: { count: number; items: Novita[] };
  replyVoice: boolean;
  ornamenti?: Ornamenti;
  /** «Una bella notizia» di Vincenzo, la più vecchia non ancora vista (solo per mamma). */
  notizia?: NotiziaDaVedere | null;
};
type Reazione = {
  id: number; author: string; kind: string; text: string | null; media_id: number | null; reply_to?: number | null; seen_by_caro?: number;
  created_at?: string;
  // «Correggi» (richiesta #24): quando l'ha corretto, e se il caro l'aveva già letto prima.
  corretto_at?: string | null; corretta_dopo_vista?: number;
};
type MemoryCard = { id: number; title: string; summary: string | null; toldAt: string; status: string; hasAudio: boolean; hasVideo?: boolean; photo: number | null; eventDate?: string | null };
type QuestionInfo = { id: number; text: string; fromSon: boolean; voiceUrl: string | null };

// I colori degli avatar sono i token --avatar-1..7 (styles.css): qui si sceglie solo l'indice dal nome.
// Le larghezze chieste al server (Fase 0): la griglia non ha bisogno dell'originale da 1,3 MB.
const LARGHEZZA_GRIGLIA = 480;
const LARGHEZZA_PAGINA_FOTO = 900;
const LARGHEZZA_LASTRA = 1600;
type PersonaRitratto = { id: number; name: string; status: string; relation: string | null };
/** Arrivata dall'albero («Scegli il ritratto dalle tue foto»): per chi è il ritratto. */
let ritrattoPer: { id: number; name: string } | null = null;
const AVATAR_TINTE = 7;
function avatarTinta(name: string): string {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) % 997;
  return `--tinta:var(--avatar-${(h % AVATAR_TINTE) + 1})`;
}
function avatar(name: string, photoMediaId: number | null | undefined): HTMLElement {
  if (photoMediaId) {
    return el('div', { class: 'cerchio', 'data-ruolo': 'volto', 'aria-hidden': 'true' }, el('img', { src: `/api/media/${photoMediaId}?w=${LARGHEZZA_GRIGLIA}`, alt: '' }));
  }
  return el('div', { class: 'cerchio', 'data-ruolo': 'volto', 'aria-hidden': 'true', style: avatarTinta(name) }, name.slice(0, 1).toUpperCase());
}

const app = document.getElementById('app') as HTMLElement;
document.querySelector('.skip-link')?.addEventListener('click', (event) => {
  event.preventDefault();
  app.focus();
});
let replyVoice = true;
// Nome della persona cara (chi riceverà il regalo): arriva da /api/state,
// con cache locale per le schermate raggiunte prima della Home.
let caro = 'Vincenzo';
try { caro = localStorage.getItem('memorie_caro') ?? caro; } catch { /* ok */ }
function setCaro(nome: string | undefined): void {
  if (!nome || nome === caro) return;
  caro = nome;
  try { localStorage.setItem('memorie_caro', nome); } catch { /* ok */ }
}

// Dimensione del testo scelta da mamma (per-dispositivo, sopravvive ai riavvii).
const FONT_KEY = 'memorie_font_scale';
function applicaScalaTesto(v: string): void {
  if (!['1', '1.15', '1.3'].includes(v)) v = '1';
  // Safari non ricalcola var() nel font-size del root: il px inline è la verità.
  document.documentElement.style.fontSize = v === '1' ? '' : `${Math.round(20 * parseFloat(v))}px`;
}
function scalaTesto(): string {
  try { return localStorage.getItem(FONT_KEY) ?? '1'; } catch { return '1'; }
}
applicaScalaTesto(scalaTesto());

// Gli ornamenti del quaderno. L'attributo arriva già nell'HTML dal server (niente lampo della
// decorazione di prima); qui lo si riallinea a ogni /api/state, così se mamma alleggerisce da
// un'altra schermata — o se lo fa Vincenzo da lontano — il quaderno la segue al tocco
// successivo, senza ricaricare l'app. La FORMA dell'album non dipende da questo: è il quaderno.
type Ornamenti = 'pieni' | 'ridotti';
const ornamenti = (): Ornamenti => (document.documentElement.dataset['ornamenti'] === 'ridotti' ? 'ridotti' : 'pieni');
/** Riallinea la decorazione. Torna true se è davvero cambiata, così chi chiama decide se ridisegnare. */
function applicaOrnamenti(valore: Ornamenti | undefined): boolean {
  if (valore !== 'pieni' && valore !== 'ridotti') return false; // una risposta vecchia non cambia nulla
  if (document.documentElement.dataset['ornamenti'] === valore) return false;
  document.documentElement.dataset['ornamenti'] = valore;
  // La barra del browser segue quello che le sta sotto: la tela, o la carta (come fa il server).
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', valore === 'ridotti' ? '#F4F6F2' : '#1E3B32');
  return true;
}

// Pulizia della schermata corrente quando si naviga via (o si chiude l'app):
// ferma il microfono e mette in salvo quello che è stato registrato.
let onLeaveScreen: (() => void) | null = null;

document.addEventListener('pointerdown', () => unlockAudio(), { once: true });
window.addEventListener('pagehide', () => {
  clearSurprises();
  const c = onLeaveScreen;
  onLeaveScreen = null;
  c?.();
});
window.addEventListener('pageshow', (event) => {
  // Il ritorno dalla cache di navigazione non ricrea la pagina né gli editor.
  if (event.persisted) void mountSurprises(app, app.dataset['schermata'] ?? 'home');
});
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => void navigator.serviceWorker.register('/sw.js').catch(() => undefined));
}

/* ---------- collegamento del dispositivo ----------
   L'app installata sulla Home dell'iPad ha cookie separati da Safari: il token
   del link magico viaggia quindi nella start_url (?t=...) e in localStorage,
   e a ogni avvio viene scambiato per il cookie. */

const TOKEN_KEY = 'memorie_family_token';
let ultimoTentativoAuth = 0;

function storedToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

function setManifestToken(t: string): void {
  document.querySelector('link[rel="manifest"]')
    ?.setAttribute('href', `/manifest.webmanifest?t=${encodeURIComponent(t)}`);
}

async function authWithToken(t: string): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/family', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: t }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

async function bootAuth(): Promise<void> {
  const fromUrl = new URLSearchParams(location.search).get('t');
  if (fromUrl) {
    try { localStorage.setItem(TOKEN_KEY, fromUrl); } catch { /* modalità privata */ }
    setManifestToken(fromUrl);
    await authWithToken(fromUrl);
    history.replaceState(null, '', '/' + location.hash);
    return;
  }
  const t = storedToken();
  if (t) setManifestToken(t);
}

async function schermataCollega(): Promise<void> {
  // Prima di disturbare: se abbiamo il token salvato, ci ricolleghiamo da soli.
  const t = storedToken();
  if (t && Date.now() - ultimoTentativoAuth > 5000) {
    ultimoTentativoAuth = Date.now();
    if (await authWithToken(t)) {
      render();
      return;
    }
  }
  screen(
    el('div', { class: 'conferma-grande' },
      el('div', { class: 'spunta' }, '🔗'),
      el('h2', {}, 'Questo tablet non è ancora collegato'),
      el('p', {}, 'Apri il link di collegamento che ti hanno mandato (il messaggio con l’indirizzo che contiene “/m/”): collegherà questo dispositivo una volta per tutte.'),
    ),
    el('button', { class: 'btn-primario', click: () => render() }, 'Riprova'),
  );
}

/* ---------- utilità ---------- */

/** Legge un testo ad alta voce. I bottoni ESPLICITI ("Leggimela", "Prova la
 *  voce") passano force: devono suonare anche con la voce automatica spenta. */
async function speak(text: string, opts?: { force?: boolean }): Promise<void> {
  if (!replyVoice && !opts?.force) return;
  // La voce del quaderno e un video insieme non si capiscono: il video aspetta.
  pausaVideo();
  try {
    const res = await fetch('/api/tts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    if (res.ok && res.status !== 204) {
      const { url } = (await res.json()) as { url: string };
      // Un file che parte ma non si sente (sull'iPad succede) non è una voce: si ripiega.
      if ((await playUrl(url)) !== 'muto') return;
    }
  } catch { /* si passa alla voce del dispositivo */ }
  await speakLocal(text);
}

// Tasto Indietro: history.back() solo se la voce PRECEDENTE della history è
// una pagina dell'app. Ogni voce dell'app viene timbrata con un indice seriale
// in history.state: così anche i gesti indietro/avanti del browser (che l'app
// non inizia) mantengono il conteggio corretto, senza derive.
// Migrazione del vecchio interruttore «lettere grandi» nel selettore a tre livelli.
try {
  if (localStorage.getItem('memorie_testo_grande') === '1' && !localStorage.getItem(FONT_KEY)) {
    localStorage.setItem(FONT_KEY, '1.15');
  }
  localStorage.removeItem('memorie_testo_grande');
} catch { /* modalità privata */ }

let navIdx = 0;

function stampHistory(): void {
  const st = history.state as { mIdx?: number } | null;
  if (st?.mIdx === undefined) {
    history.replaceState({ mIdx: navIdx }, '');
  } else {
    navIdx = st.mIdx;
  }
}

function indietro(): void {
  stopSpeaking();
  if (navIdx > 0) history.back();
  else location.hash = '#home';
}

function testata(titolo: string): HTMLElement {
  return el(
    'div',
    { class: 'testata', 'data-ruolo': 'testata' },
    el('button', { class: 'btn-casa', 'data-ruolo': 'indietro', 'aria-label': 'Torna indietro', click: indietro }, el('span', { 'aria-hidden': 'true' }, '‹ '), 'Indietro'),
    el('button', { class: 'btn-casa', 'data-ruolo': 'casa', 'aria-label': 'Torna alla pagina iniziale', click: () => { stopSpeaking(); location.hash = '#home'; } }, el('span', { 'aria-hidden': 'true' }, '⌂ '), 'Casa'),
    el('h1', {}, titolo),
  );
}

/* ---------- navigazione adattiva: dock, rail, barra laterale ---------- */

const barraMenu = el('nav', { class: 'barra-menu', 'data-ruolo': 'navigazione', 'aria-label': 'Menu principale' },
  ...VOCI_BARRA.map((v) =>
    el('button', { 'data-hash': v.hash, 'data-ruolo': 'destinazione', click: () => { stopSpeaking(); location.hash = v.hash; } },
      icon(v.icona), el('span', {}, v.label),
      v.hash === '#home' ? el('span', { class: 'pallino', hidden: '' }) : null,
    ),
  ),
);
const familyNavigation = el('div', { class: 'family-navigation' },
  el('a', { class: 'family-brand', href: '#home', 'aria-label': 'Memorie, pagina iniziale' },
    icon('book'), el('span', {}, 'memorie', el('small', {}, 'Il quaderno di famiglia')),
  ),
  barraMenu,
  el('nav', { class: 'family-library', 'aria-label': 'Sfoglia il quaderno' },
    el('p', {}, 'IL TUO QUADERNO'),
    ...([['photos', '#foto', 'Fotografie'], ['family', '#albero', 'La famiglia'], ['book', '#libro', 'Il tuo libro'], ['clock', '#cronologia', 'Nel tempo']] as const)
      .map(([name, href, label]) => el('a', { href }, icon(name), label)),
  ),
  el('div', { class: 'family-nav-footer' },
    el('a', { href: '#impostazioni' }, icon('settings'), 'Impostazioni'),
    el('a', { href: '#aiuto' }, icon('help'), 'Come si usa'),
  ),
);
document.body.append(familyNavigation);

// Il pallino rosso sulla «Casa»: quante novità da Vincenzo aspettano mamma.
// novitaViste = l'ultimo conteggio noto (serve a capire se ne è arrivata una nuova).
let novitaNote = 0;
let novitaViste = -1;
function aggiornaPallino(n: number): void {
  novitaNote = n;
  novitaViste = n;
  const pallino = barraMenu.querySelector<HTMLElement>('.pallino');
  if (!pallino) return;
  pallino.textContent = n > 9 ? '9+' : String(n);
  pallino.hidden = n === 0;
  pallino.setAttribute('aria-label', `${n} novità da ${caro}`);
}

function aggiornaBarra(route: string): void {
  // Durante la registrazione la barra sparisce: una sola azione principale,
  // niente tocchi accidentali che interrompono la voce di mamma.
  const nascosta = route === 'registra';
  document.body.classList.toggle('registrando', nascosta);
  document.body.classList.toggle('con-barra', !nascosta);
  // Tutte le modalità di voce stanno sotto #registra: da qui alla fine la linea è sua, e un
  // video in viaggio interrompe subito il pezzo in volo (lib/video-upload.ts).
  if (nascosta) annunciaVoce();
  const bottoni = barraMenu.querySelectorAll('button');
  VOCI_BARRA.forEach((v, i) => {
    const attiva = v.match.includes(route);
    const b = bottoni[i]!;
    b.classList.toggle('attiva', attiva);
    if (attiva) b.setAttribute('aria-current', 'page');
    else b.removeAttribute('aria-current');
  });
  for (const link of familyNavigation.querySelectorAll('.family-library a, .family-nav-footer a')) {
    if (link.getAttribute('href') === `#${route}`) link.setAttribute('aria-current', 'page');
    else link.removeAttribute('aria-current');
  }
}

/* ---------- il posto in cui era ----------
 * Mamma scorre diciotto storie, ne apre una, la legge, tocca «Indietro» — e si ritrova in
 * cima. Con il quaderno che cresce, quel salto le costa ogni volta la stessa risalita, e la
 * storia che stava per aprire non la trova più. Il punto si ricorda per voce di cronologia
 * (`mIdx`, che l'app già timbra): tornando indietro si riapre dov'era, andando avanti si
 * parte dall'inizio, come è giusto. */
const posti = new Map<number, number>();
/** Vero solo per la prima `screen()` dopo un gesto «indietro»: i ridisegni successivi non
 *  devono strappare la pagina sotto le dita mentre lei sta già scorrendo. */
let postoDaRiprendere: number | null = null;

function ricordaIlPosto(): void {
  posti.set(navIdx, window.scrollY);
}

let riprendiTimer: number | undefined;
let ultimoImposto = -1;

function riprendiIlPosto(): void {
  window.clearTimeout(riprendiTimer);
  const y = postoDaRiprendere;
  if (y === null || y <= 0) { postoDaRiprendere = null; window.scrollTo(0, 0); return; }

  // La pagina appena disegnata è quasi sempre più corta di quanto sarà: le fotografie
  // devono ancora arrivare, e `scrollTo` si ferma dove finisce il foglio. Si insiste per
  // un secondo scarso, e ci si ferma appena si è arrivati — o appena è lei a muoversi,
  // perché nessuno deve sentirsi strappare la pagina da sotto le dita.
  const tappe = [0, 60, 150, 320, 600, 950];
  let i = 0;
  const prova = (): void => {
    if (ultimoImposto >= 0 && Math.abs(window.scrollY - ultimoImposto) > 2) { postoDaRiprendere = null; return; }
    window.scrollTo(0, y);
    ultimoImposto = window.scrollY;
    if (Math.abs(window.scrollY - y) <= 2 || i >= tappe.length) { postoDaRiprendere = null; return; }
    riprendiTimer = window.setTimeout(prova, tappe[i++]! );
  };
  ultimoImposto = -1;
  prova();
}

let screenLoadTimer: number | undefined;
function screen(...children: Array<Node | string | null | undefined>): void {
  clearSurprises();
  window.clearTimeout(screenLoadTimer);
  stopSpeaking();
  fermaVideo();
  clear(app);
  app.append(...children.filter((c): c is Node | string => c !== null && c !== undefined));
  app.removeAttribute('aria-busy');
  // Quale schermata è DAVVERO sullo schermo, non quale è stata chiesta. Fra il cambio di
  // indirizzo e la fine della sua attesa, la pagina vecchia resta lì con il suo titolo:
  // chi guarda da fuori (i test, e chiunque debba sapere che cosa si vede) senza questo
  // segnale misura la pagina precedente credendola nuova.
  app.dataset['disegnata'] = app.dataset['schermata'] ?? 'home';
  const heading = app.querySelector<HTMLElement>('h1, h2');
  if (heading) {
    document.title = `${heading.textContent} · Memorie`;
    heading.tabIndex = -1;
    heading.focus({ preventScroll: true });
  }
  riprendiIlPosto();
  void mountSurprises(app, app.dataset['schermata'] ?? 'home');
}

window.addEventListener('memorie:action-error', () => {
  document.querySelector('.avviso-azione')?.remove();
  const notice = el('div', { class: 'avviso avviso-azione', role: 'alert' },
    el('span', {}, 'Non sono riuscita a completare questa azione. Riprova tra poco.'),
    el('button', { class: 'btn-quieto', click: () => notice.remove() }, 'Chiudi'),
  );
  document.body.append(notice);
});

/** Aspetta che una condizione diventi vera (con timeout). */
async function waitUntil(cond: () => boolean, timeoutMs: number): Promise<void> {
  const start = Date.now();
  while (!cond() && Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, 200));
  }
}

function schermataErrore(err: unknown): void {
  if (err instanceof ApiError && err.status === 401) {
    void schermataCollega();
    return;
  }
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  screen(
    testata('Memorie'),
    el('div', { class: 'avviso' },
      offline
        ? 'In questo momento il tablet non è collegato a internet. Le tue registrazioni sono al sicuro: riprova tra un po’.'
        : 'Non riesco a caricare la pagina. Controlla che il tablet sia collegato a internet e riprova.'),
    el('button', { class: 'btn-primario', click: () => render() }, 'Riprova'),
  );
  console.error(err);
}

/* ---------- Home ---------- */

async function renderHome(): Promise<void> {
  const s = await api.get<HomeState>('/api/state?casa=1&domande=1');
  replyVoice = s.replyVoice;
  setCaro(s.caroName);
  applicaOrnamenti(s.ornamenti);
  // PWA rimasta aperta per giorni: quando il server è stato aggiornato, si ricarica da sola.
  // MAI con un video in viaggio: ricaricare perderebbe il file a metà strada. Si rimanda, senza
  // segnare la versione nuova, così la prima Casa dopo la fine del caricamento ricarica.
  try {
    const known = localStorage.getItem('memorie_build');
    if (s.appBuild && known && known !== s.appBuild && !sessionStorage.getItem('memorie_ricaricata')) {
      if (!caricatore.attivo()) {
        sessionStorage.setItem('memorie_ricaricata', '1');
        localStorage.setItem('memorie_build', s.appBuild);
        location.reload();
        return;
      }
    } else if (s.appBuild) localStorage.setItem('memorie_build', s.appBuild);
  } catch { /* modalità privata */ }

  const pezzi: Array<Node> = [el('header', { class: 'home-heading' },
    el('div', { 'data-surprise-anchor': 'home' }, el('p', { class: 'eyebrow' }, 'IL TUO SPAZIO, LE TUE STORIE'), el('h1', { class: 'saluto' }, `Ciao, ${s.name}`)),
    el('p', { class: 'data-oggi' }, fmtDate(s.date)),
  )];
  // «Una bella notizia»: in cima, una alla volta, finché lei non tocca «Che bello!». Quando c'è,
  // «Una cosa nuova» e i consigli aspettano (mai insieme): non si chiama nemmeno `cartaConsiglio`,
  // che segnerebbe «mostrato» un consiglio che lei non ha visto.
  const bellaNotizia = s.notizia ?? null;
  if (bellaNotizia) pezzi.push(cartaNotizia(bellaNotizia, () => render()));
  pezzi.push(
    el('section', { class: 'home-hero', 'aria-labelledby': 'invito-title' },
      el('div', { class: 'hero-copy' },
        el('p', { class: 'eyebrow' }, icon('mic'), 'UN MOMENTO PER TE'),
        el('h2', { id: 'invito-title' }, 'Ogni ricordo merita una pagina.'),
        el('p', { class: 'invito-racconto' }, 'Un volto, un luogo, un momento. Ti ascolto.'),
        el('button', { class: 'btn-primario home-racconta', 'data-ruolo': 'racconta', click: () => { location.hash = '#scegli'; } }, icon('mic'), 'Racconta un ricordo'),
      ),
      el('div', { class: 'hero-art', 'aria-hidden': 'true' },
        el('div', { class: 'album-sheet album-sheet-back' }),
        el('div', { class: 'album-sheet' }, icon('book'), el('span', {}, 'Le nostre', el('br'), 'storie.')),
      ),
    ),
    el('a', { class: 'home-collection', href: '#libro' },
      icon('book'),
      el('div', { class: 'collection-heading' }, el('span', { class: 'eyebrow' }, 'UNA PAGINA ALLA VOLTA'), el('h2', {}, 'Il tuo libro cresce con te')),
      el('div', { class: 'collection-counts' },
        el('div', {}, el('strong', {}, String(s.book.memories)), el('span', {}, s.book.memories === 1 ? 'ricordo' : 'ricordi')),
        el('div', {}, el('strong', {}, String(s.book.chapters)), el('span', {}, s.book.chapters === 1 ? 'capitolo' : 'capitoli')),
      ),
      el('span', { class: 'collection-link' }, s.book.memories ? 'Apri il tuo libro' : 'La prima pagina ti aspetta', icon('arrow')),
    ),
  );

  if (s.heartPlaces) pezzi.push(el('a', { class: 'heart-places-invitation', href: '#luoghi', 'data-ruolo': 'regalo-luoghi' },
    el('span', { class: 'heart-eyebrow' }, `UN REGALO DA ${s.heartPlaces.sender.toLocaleUpperCase('it')}`),
    el('strong', {}, s.heartPlaces.title),
    el('span', {}, 'Ho raccolto un piccolo viaggio tra i tuoi racconti. Lo facciamo insieme?'),
    el('span', {}, 'Apri il tuo regalo →')));

  // «Novità da Vincenzo»: cuori, messaggi, domande e voci degli ospiti in una
  // carta sola, ben visibile, con il conteggio (come una notifica).
  // La domanda sulla carta di oggi non è più fra le novità: la Casa chiede /api/state?casa=1 e il
  // server la segna vista prima di contarle (richiesta #41).
  const nov = s.novita;
  const conta = nov?.count ?? 0;
  aggiornaPallino(conta);
  // La domanda già mostrata sotto non va ripetuta anche nell'anteprima delle novità (con l'admin,
  // che non la segna vista, ci sarebbe ancora).
  const fromCaro = s.questionsFromCaro ?? [];
  const idsFromCaro = new Set(fromCaro.map((q) => q.id));
  const inCarta = new Set([s.todayQuestion?.id, ...(s.todayQuestions ?? []).map((q) => q.id), ...idsFromCaro]);
  const homeNovita = nov?.items.filter((n) => !(n.type === 'domanda' && inCarta.has(n.questionId))) ?? [];
  if (nov && homeNovita.length > 0) {
    pezzi.push(
      el('div', { class: 'carta carta-vincenzo carta-novita' },
        el('p', { class: 'etichetta' }, `🔔 Novità da ${caro}`, el('span', { class: 'conta' }, String(conta))),
        ...homeNovita.slice(0, 2).map((n) => rigaNovita(n)),
        el('button', { class: 'btn-secondario mt-3', click: () => { location.hash = '#novita'; } },
          conta === 1 ? 'Guarda e rispondi' : `Guarda tutte e rispondi (${conta})`),
      ),
    );
  }

  // «Una cosa nuova»: il quaderno sistema le piccole cose, con una prova guidata senza rischi.
  // «Più tardi» la rimanda di tre giorni; a prova finita non torna più (chiave in views/quaderno.ts).
  if (!bellaNotizia) {
    if (annuncioDaMostrare()) {
      const annuncio = el('div', { class: 'carta carta-annuncio' },
        el('p', { class: 'etichetta' }, '✨ Una cosa nuova'),
        el('p', { class: 'domanda-testo' }, `Adesso puoi dire al quaderno cosa non va in una storia o in una foto: una data, un nome, una foto doppia… Lui la sistema subito. E se cambi idea, un tocco su “Annulla” rimette tutto com’era. Le cose grandi le passa a ${caro}, che ti risponde qui.`),
        el('button', { class: 'btn-primario', click: () => { location.hash = '#chiedi?prova=1'; } }, '✨ Provalo con me'),
        el('button', { class: 'btn-quieto', click: () => { segnaAnnuncio('rimandato'); annuncio.remove(); } }, 'Più tardi'),
      );
      pezzi.push(annuncio);
    } else {
      // «💡 Una cosa che forse non sai»: un consiglio alla volta, nello stesso posto (mai insieme).
      pezzi.push(cartaConsiglio(caro));
    }
  }

  // «Accadde oggi»: le date vere delle sue storie e i compleanni di famiglia.
  for (const a of s.accadde ?? []) {
    const testo =
      a.kind === 'oggi' ? `Oggi, nel ${a.anno}: «${a.title ?? ''}»`
      : a.kind === 'mese' ? `In questo mese, nel ${a.anno}: «${a.title ?? ''}»`
      : a.kind === 'compleanno' ? `Oggi è il compleanno di ${a.name}${a.anni ? `: ${a.anni} anni` : ''} 🎂`
      : `Oggi era il compleanno di ${a.name} 🕯️`;
    pezzi.push(
      el('div', { class: 'carta' },
        el('p', { class: 'etichetta' }, '🗓 Accadde oggi'),
        el('p', { class: 'domanda-testo' }, testo),
        a.storyId ? el('button', { class: 'btn-secondario', click: () => { location.hash = `#storia/${a.storyId}`; } }, 'Rileggi la storia') : null,
        a.personId ? el('button', { class: 'btn-secondario', click: () => { location.hash = `#persona/${a.personId}`; } }, 'La sua pagina') : null,
      ),
    );
  }

  if (fromCaro.length) pezzi.push(cartaDomande(fromCaro, true));
  const altre = s.todayQuestions?.filter((q) => !idsFromCaro.has(q.id));
  if (altre && altre.length > 0) {
    pezzi.push(cartaDomande(altre));
  } else if (!s.todayQuestions && s.todayQuestion && !idsFromCaro.has(s.todayQuestion.id)) {
    const q = s.todayQuestion;
    pezzi.push(
      el(
        'div',
        { class: 'carta home-question' + (q.fromSon ? ' carta-vincenzo' : ''), 'data-ruolo': 'domanda-del-giorno' },
        el('p', { class: 'etichetta' }, q.fromSon ? `${caro} ti ha lasciato una domanda` : 'La domanda di oggi'),
        el('p', { class: 'domanda-testo' }, q.text),
        q.voiceUrl
          ? el('button', { class: 'btn-secondario mb-3', 'data-ruolo': 'voce', click: () => void playUrl(q.voiceUrl!) }, '▶  Ascolta la sua voce')
          : null,
        q.ricetta
          ? el('button', { class: 'btn-secondario', click: () => { location.hash = `#registra?mode=dialogo&ricetta=1&q=${q.id}`; } }, '🍳  Raccontami la ricetta')
          : el('button', { class: 'btn-secondario', click: () => { location.hash = `#registra?mode=dialogo&q=${q.id}`; } }, '🎙️  Rispondi a questa domanda'),
        el('div', {},
          el('button', {
            class: 'btn-quieto',
            click: async () => { await api.post('/api/questions/today/swap'); render(); },
          }, 'Preferisco un’altra domanda'),
          el('button', {
            class: 'btn-quieto',
            click: async () => { await api.post('/api/questions/today/skip'); render(); },
          }, 'Non voglio parlare di questo'),
        ),
      ),
    );
  }

  if (s.counts.verifiche > 0) {
    pezzi.push(
      el('div', { class: 'carta' },
        el('p', { class: 'etichetta' }, '🌳 Mettiamo in ordine'),
        el('p', { class: 'domanda-testo' },
          `Dai racconti ho raccolto ${s.counts.verifiche === 1 ? 'una cosa' : `${s.counts.verifiche} cose`} da controllare con te: nomi, date, parentele. Mi aiuti?`),
        el('button', { class: 'btn-secondario', click: () => { location.hash = '#ordine'; } }, 'Sì, controlliamo insieme'),
      ),
    );
  }

  // «Una storia dal baule»: ogni giorno riaffiora una storia diversa.
  if (s.baule) {
    const b = s.baule;
    // L'anno, o il decennio se lei ha detto solo quello: «1950» per «195X» direbbe troppo.
    const quandoBaule = b.data && annoIncompleto(b.data) ? fmtDate(b.data) : b.anno;
    pezzi.push(
      el('div', { class: 'carta carta-baule', click: () => { location.hash = `#storia/${b.id}`; } },
        el('p', { class: 'etichetta' }, '🧳 Dal baule dei ricordi'),
        b.photoId ? el('img', { class: 'baule-foto', src: `/api/media/${b.photoId}?w=${LARGHEZZA_GRIGLIA}`, alt: '' }) : null,
        el('p', { class: 'domanda-testo' }, `Ti ricordi questa? «${b.title ?? 'Una tua storia'}»${quandoBaule ? ` — ${quandoBaule}` : ''}`),
        el('button', { class: 'btn-secondario', click: () => { location.hash = `#storia/${b.id}`; } }, 'Rileggila'),
      ),
    );
  }

  pezzi.push(homeShortcuts());

  pezzi.push(
    el('div', { class: 'piede-home' },
      el('button', { class: 'btn-quieto', click: () => { location.hash = '#aiuto'; } }, '🛟 Come si usa'),
      el('button', { class: 'btn-quieto', click: () => { location.hash = '#impostazioni'; } }, '⚙️ Impostazioni'),
      el('button', { class: 'btn-quieto', click: () => {
        const v = scalaTesto() === '1' ? '1.15' : '1';
        try { localStorage.setItem(FONT_KEY, v); } catch { /* privata */ }
        applicaScalaTesto(v);
        render();
      } }, scalaTesto() === '1' ? 'Aa Lettere più grandi' : 'Aa Lettere normali'),
    ),
  );

  screen(el('div', { class: 'home-workspace' }, ...pezzi));
}

/**
 * Le domande del caro restano tutte disponibili; gli altri spunti si sfogliano in tre. Sfogliava la
 * domanda del giorno a raffiche di tocchi cercandone una che le piacesse, e ogni «Non voglio
 * parlare di questo» la ritirava per sempre. Qui sceglie a colpo d'occhio; «Altre domande» ne
 * porta tre nuove senza ritirare niente, e il ritiro è un gesto secondo, dietro «⋯».
 */
function cartaDomande(domande: TodayQuestion[], delCaro = false): HTMLElement {
  const da = (q: TodayQuestion): string =>
    q.tipo === 'vincenzo' || q.fromSon ? `Da ${caro}`
    : q.tipo === 'albero' ? 'Dal tuo albero'
    : q.tipo === 'suo' ? 'Un tuo spunto'
    : q.tipo === 'storia' ? 'Da una tua storia'
    : 'Un ricordo da cercare';
  const riga = (q: TodayQuestion): HTMLElement =>
    el('div', { class: 'domanda-scelta' + (q.fromSon ? ' da-vincenzo' : ''), 'data-ruolo': 'domanda-scelta', 'data-tipo': q.tipo ?? '' },
      el('p', { class: 'domanda-da' }, da(q)),
      el('p', { class: 'domanda-testo' }, q.text),
      q.voiceUrl
        ? el('button', { class: 'btn-secondario mb-3', 'data-ruolo': 'voce', click: () => void playUrl(q.voiceUrl!) }, '▶  Ascolta la sua voce')
        : null,
      q.ricetta
        ? el('button', { class: 'btn-secondario', 'data-ruolo': 'rispondi', click: () => { location.hash = `#registra?mode=dialogo&ricetta=1&q=${q.id}`; } }, '🍳  Raccontami la ricetta')
        : el('button', { class: 'btn-secondario', 'data-ruolo': 'rispondi', click: () => { location.hash = `#registra?mode=dialogo&q=${q.id}`; } }, '🎙️  Rispondi'),
      el('details', { class: 'domanda-altro' },
        el('summary', { 'aria-label': 'Altre scelte per questa domanda' }, '⋯'),
        el('button', {
          class: 'btn-quieto', 'data-ruolo': 'non-voglio',
          click: async () => { await api.post(delCaro ? `/api/questions/${q.id}/ritira` : '/api/questions/today/skip', { id: q.id }); render(); },
        }, 'Non voglio parlare di questo'),
      ),
    );
  return el('div', { class: 'carta home-question home-domande', 'data-ruolo': delCaro ? 'domande-del-caro' : 'domanda-del-giorno' },
    el('p', { class: 'etichetta' }, delCaro ? `Le domande di ${caro}` : 'Di cosa ti va di parlare oggi?'),
    delCaro ? el('p', {}, 'Scegli la domanda che ti va. Puoi rispondere a una o a più domande, anche in momenti diversi. Le altre restano qui per quando vorrai.') : null,
    ...domande.map(riga),
    !delCaro ? el('button', {
      class: 'btn-quieto', 'data-ruolo': 'altre-domande',
      click: async () => { await api.post('/api/questions/today/altre'); render(); },
    }, 'Altre domande') : null,
  );
}

/* ---------- il filo con Vincenzo: novità, risposte, avvisi ---------- */

// Icona, frase e bottone d'ascolto di una novità vivono in lib/filo-ui.ts: qui e in
// «La tua posta» devono essere gli stessi (il nome del caro arriva quando /api/state risponde).
const { iconaNovita, fraseNovita, azioneNovita } = filoUI({ caro: () => caro, speak });

/** Il «visto» di una domanda del caro. Torna quanto scalare dal pallino: 0 se era già vista,
 *  o se a guardare è l'admin (il server la lascia a lei: 403). */
async function segnaDomandaVista(id: number): Promise<number> {
  try { return (await api.post<{ vista: number }>(`/api/questions/${id}/vista`)).vista; } catch { return 0; }
}

function rigaNovita(n: Novita): HTMLElement {
  const text = n.type === 'reazione' && n.kind === 'text' && n.text
    ? `${caro}: “${n.text.length > 110 ? n.text.slice(0, 110) + '…' : n.text}”`
    : fraseNovita(n, true);
  return el('div', { class: 'novita-riga' },
    el('span', { class: 'novita-icona', 'aria-hidden': 'true' }, iconaNovita(n)),
    el('div', { class: 'novita-testo' }, el('div', {}, text), el('div', { class: 'quando' }, quandoRelativo(n.when))),
    azioneNovita(n),
  );
}

/**
 * «Da vedere»: le novità di oggi, in cima alla posta. Una sola porta per tutto quello che
 * arriva da Vincenzo (prima c'erano Novità, Posta e la carta in Casa, e lei non ritrovava più
 * un vocale). Qui i pulsanti «L'ho visto» e «Rispondi» ci sono come prima; quello che ha visto
 * scende nell'archivio sotto. Torna null se non c'è niente di nuovo.
 */
async function sezioneDaVedere(): Promise<{ element: HTMLElement; chiavi: Set<string> } | null> {
  const nov = await api.get<{ count: number; items: Novita[]; precedenti: Novita[] }>('/api/novita');
  aggiornaPallino(nov.count);
  novitaViste = nov.count;
  if (nov.items.length === 0) return null;
  const chiavi = new Set(nov.items.map((n) => `${n.type}:${n.id}`));
  return { chiavi, element: el('section', { class: 'posta-da-vedere', 'aria-labelledby': 'da-vedere-titolo' },
    el('h2', { class: 'sezione', id: 'da-vedere-titolo' }, 'Da vedere'),
    ...nov.items.map((n) => cartaNovita(n, false)),
    nov.items.filter((n) => n.type === 'reazione').length > 1
      ? el('button', { class: 'btn-quieto', click: async () => { await api.post('/api/reactions/seen-all'); render(); } }, '✓  Le ho viste tutte')
      : null,
  ) };
}

function cartaNovita(n: Novita, precedente: boolean): HTMLElement {
  const area = el('div', { class: 'azioni' });
  const carta = el('div', { class: `carta carta-vincenzo novita-card${precedente ? ' novita-vista' : ''}`, ...(n.type === 'richiesta' ? { 'data-request-id': String(n.requestId) } : {}) },
    el('p', { class: 'etichetta' }, `${iconaNovita(n)} ${quandoRelativo(n.when)}`),
    el('p', { class: 'domanda-testo' }, fraseNovita(n)),
    n.type === 'reazione' && n.kind === 'text' && n.text && !soloEmoji(n.text) ? el('p', { class: 'citazione' }, `“${n.text}”`) : null,
    n.type === 'richiesta' && n.text ? el('p', { class: 'citazione' }, `“${n.text}”`) : null,
    area,
  );
  const vaiStoria = (id: number) => el('button', { class: 'btn-secondario', click: () => { location.hash = `#storia/${id}`; } }, '📖  Vai alla storia');

  function mostraAzioni(): void {
    if (n.type === 'domanda') {
      // Ascoltare la sua voce è già averla vista; «L'ho vista» la toglie da qui senza rispondere.
      // Resta nella posta e sulla carta della domanda del giorno: si risponde quando si vuole.
      const vista = async (): Promise<void> => {
        const tolte = await segnaDomandaVista(n.questionId);
        if (tolte) aggiornaPallino(Math.max(0, novitaNote - tolte));
      };
      const voce = azioneNovita(n, 'btn-secondario');
      voce?.addEventListener('click', () => void vista());
      area.replaceChildren(
        voce ?? '',
        el('button', { class: 'btn-primario', click: () => { location.hash = `#registra?mode=dialogo&q=${n.questionId}`; } }, '🎙️  Rispondi raccontando'),
        ...(precedente ? [] : [el('button', { class: 'btn-quieto', 'data-ruolo': 'domanda-vista', click: async () => {
          await vista();
          carta.remove();
        } }, '✓  L’ho vista')]),
      );
      return;
    }
    if (n.type === 'voce') {
      area.replaceChildren(el('button', { class: 'btn-primario', click: () => { location.hash = `#storia/${n.memoryId}`; } }, '🎧  Ascoltalo'));
      return;
    }
    if (n.type === 'richiesta') {
      area.replaceChildren(
        azioneNovita(n, 'btn-secondario') ?? '',
        el('button', { class: 'btn-primario', click: () => { location.hash = `#chiedi?conversazione=${n.requestId}`; } }, '💬  Vai alla conversazione'),
        ...(precedente ? [] : [el('button', { class: 'btn-quieto', click: async () => {
          try { await api.post(`/api/quaderno/${n.requestId}/vista`); } catch { /* riproverà al prossimo giro */ }
          // Il server segna viste TUTTE le risposte di quella conversazione: via tutte le sue carte.
          const stesse = document.querySelectorAll(`.novita-card[data-request-id="${n.requestId}"]:not(.novita-vista)`);
          const tolte = Math.max(1, stesse.length);
          stesse.forEach((c) => c.remove());
          carta.remove();
          aggiornaPallino(Math.max(0, novitaNote - tolte));
        } }, '✓  Ho visto')]),
      );
      return;
    }
    area.replaceChildren(
      azioneNovita(n, 'btn-secondario') ?? '',
      el('button', { class: 'btn-primario', click: () => apriRisposta(n.memoryId, area, { onDone: (r) => esitoRisposta(area, r, mostraAzioni), onCancel: mostraAzioni }) }, `💬  Rispondi a ${caro}`),
      el('div', { class: 'azioni-riga' },
        vaiStoria(n.memoryId),
        precedente ? null : el('button', { class: 'btn-quieto', click: async () => {
          try { await api.post(`/api/reactions/${n.id}/seen`); } catch { /* riproverà */ }
          carta.remove();
          aggiornaPallino(Math.max(0, novitaNote - 1));
        } }, '✓  L’ho visto'),
      ),
    );
  }
  mostraAzioni();
  return carta;
}

/** Dopo l'invio: conferma con «Ritira» a portata di tocco (azione morbida, senza domande prima). */
function esitoRisposta(area: HTMLElement, r: Risposta, indietro: () => void): void {
  area.replaceChildren(
    el('div', { class: 'carta centrato mb-0' },
      el('p', { class: 'domanda-testo mb-2' }, `La tua risposta è partita ✓`),
      el('p', {}, `${caro} la troverà appena apre il quaderno.`),
      el('button', { class: 'btn-quieto', click: async () => {
        try { await api.del(`/api/reactions/${r.id}`); } catch { /* già vista: resta */ }
        indietro();
      } }, '↩️  Ritira la risposta'),
    ),
  );
}

/** Il composer «Rispondi a Vincenzo»: voce o scritto, AL POSTO dei pulsanti toccati. */
function apriRisposta(memoryId: number, area: HTMLElement, opts: { onDone: (r: Risposta) => void; onCancel: () => void }): void {
  function scelta(): void {
    area.replaceChildren(
      el('div', { class: 'carta risposta-box' },
        el('p', { class: 'etichetta' }, `Rispondi a ${caro}`),
        el('button', { class: 'btn-primario', click: () => void aVoce() }, '🎙️  Rispondi a voce'),
        el('button', { class: 'btn-secondario mt-3', click: scrivendo }, '✏️  Rispondi scrivendo'),
        el('button', { class: 'btn-quieto', click: opts.onCancel }, 'Annulla'),
      ),
    );
    area.scrollIntoView({ block: 'center' });
  }
  function scrivendo(): void {
    const ta = el('textarea', { class: 'scrivi-testo grande', rows: '4', 'aria-label': `La tua risposta a ${caro}`, placeholder: 'Scrivi qui…' }) as HTMLTextAreaElement;
    const invia = el('button', { class: 'btn-verde' }, 'Invia ✓') as HTMLButtonElement;
    const esito = el('p', { role: 'status' }, '');
    invia.addEventListener('click', async () => {
      const text = ta.value.trim();
      if (text.length < 1) { esito.className = 'avviso'; esito.textContent = 'Scrivi qualcosa prima di inviare.'; return; }
      invia.disabled = true;
      try {
        opts.onDone(await api.post<Risposta>(`/api/memories/${memoryId}/replies`, { text }));
      } catch {
        esito.className = 'avviso';
        esito.textContent = 'Non riesco a inviare adesso: riprova tra un momento.';
        invia.disabled = false;
      }
    });
    area.replaceChildren(
      el('div', { class: 'scrivi-area' }, ta, invia, esito),
      el('button', { class: 'btn-quieto', click: scelta }, 'Annulla'),
    );
    ta.focus();
  }
  async function aVoce(): Promise<void> {
    const stato = el('p', { class: 'stato-ascolto', role: 'status' }, 'Un momento…');
    const fine = el('button', { class: 'btn-verde' }, 'Ho finito ✓') as HTMLButtonElement;
    const annulla = el('button', { class: 'btn-quieto' }, 'Annulla') as HTMLButtonElement;
    area.replaceChildren(
      el('div', { class: 'carta risposta-box' },
        el('p', { class: 'domanda-testo' }, `Parla pure: ${caro} ascolterà la tua voce`),
        stato, fine, annulla),
    );
    const rec = new Recorder({ onError: (msg) => { stato.textContent = msg; } });
    try {
      await rec.start({ purpose: 'reaction' });
      stato.textContent = 'Ti ascolto…';
    } catch {
      stato.textContent = 'Non riesco a usare il microfono: prova con «Rispondi scrivendo».';
      fine.hidden = true;
      annulla.addEventListener('click', scelta);
      return;
    }
    // Se cambia schermata a metà, il microfono si spegne (niente upload fantasma).
    const spegni = (): void => { void rec.stop().catch(() => undefined); };
    onLeaveScreen = spegni;
    const lascia = (): void => { if (onLeaveScreen === spegni) onLeaveScreen = null; };
    annulla.addEventListener('click', async () => {
      lascia();
      try { await rec.stop(); } catch { /* ok */ }
      scelta();
    });
    fine.addEventListener('click', async () => {
      fine.disabled = true;
      lascia();
      stato.textContent = 'Invio la tua voce…';
      try {
        const { recordingId, durationS, chunkCount } = await rec.stop();
        const { mediaId } = await finishRecording<{ mediaId: number }>(recordingId, { durationS, expectedChunks: chunkCount });
        void deleteRecording(recordingId);
        opts.onDone(await api.post<Risposta>(`/api/memories/${memoryId}/replies`, { mediaId }));
      } catch {
        stato.textContent = 'Non sono riuscita a inviarla: la tua voce è salvata, riprova.';
        fine.disabled = false;
      }
    });
  }
  scelta();
}

/** Il filo dentro la storia: le bolle di Vincenzo e le risposte di mamma, poi «Rispondi». */
function cartaFilo(memoryId: number, reactions: Reazione[], ricarica: () => void): HTMLElement {
  const area = el('div', {});
  const rispondi = el('button', { class: 'btn-primario', click: () => apriRisposta(memoryId, area, { onDone: () => ricarica(), onCancel: mostra }) }, `💬  Rispondi a ${caro}`);
  function mostra(): void { area.replaceChildren(rispondi); }
  mostra();
  return el('div', { class: 'carta carta-vincenzo filo' },
    el('p', { class: 'etichetta' }, `💌 Il filo con ${caro}`),
    el('div', { class: 'chat filo-chat' }, ...reactions.map((r) => bollaFilo(r, ricarica))),
    area,
  );
}

function bollaFilo(r: Reazione, ricarica: () => void, detto?: string): HTMLElement {
  const mia = r.author === 'mamma';
  const contenuto: Array<Node | string> = [];
  if (r.kind === 'heart') contenuto.push('♥ Questa storia mi è piaciuta tanto');
  else if (r.kind === 'text') contenuto.push(el('div', { class: soloEmoji(r.text ?? '') ? 'emoji-grande' : '' }, r.text ?? ''));
  else {
    contenuto.push(el('button', { class: 'btn-mini', 'data-ruolo': 'voce', click: () => void playUrl(`/api/media/${r.media_id}`) }, mia ? '▶  La tua risposta' : '▶  Ascolta il messaggio'));
    if (mia && r.text) contenuto.push(el('div', { class: 'quando mt-2' }, `“${r.text}”`));
  }
  const quando = quandoRelativo(r.created_at ?? '');
  // «corretto» accanto all'ora, piccolo: lo vede anche lui, nell'admin.
  const meta = mia
    ? el('div', { class: 'filo-meta' }, `Tu · ${quando}`,
        r.corretto_at ? el('span', { class: 'filo-corretto', 'data-ruolo': 'corretto' }, ' · corretto') : null,
        r.seen_by_caro ? ` · ${caro} l’ha vista ✓`
          : r.corretta_dopo_vista ? ` · ${caro} non ha ancora visto la correzione` : ' · non ancora vista')
    : el('div', { class: 'filo-meta' }, `${caro} · ${quando}`);
  // «Ritira» solo finché lui non l'ha mai letta: una correzione la rimette «da vedere», ma
  // togliergli una cosa già letta no.
  const ritirabile = mia && !r.seen_by_caro && !r.corretta_dopo_vista;
  // «Correggi» su ogni suo messaggio che ha delle parole: di un vocale, la trascrizione.
  const correggibile = mia && (r.kind === 'text' || r.kind === 'voice') && Boolean(r.text);
  const correggi = correggibile
    ? el('button', { class: 'btn-quieto', 'data-ruolo': 'correggi-messaggio', click: () => apriCorrezione() }, '✏️  Correggi') as HTMLButtonElement
    : null;
  const bolla = el('div', { class: `bolla ${mia ? 'bolla-mamma filo-mia' : 'bolla-caro'}` },
    ...contenuto,
    meta,
    correggi,
    ritirabile
      ? el('button', { class: 'btn-quieto', click: async () => { try { await api.del(`/api/reactions/${r.id}`); } catch { /* già vista */ } ricarica(); } }, '↩️  Ritira')
      : null,
    detto ? el('p', { class: 'filo-meta filo-fatto', role: 'status' }, detto) : null,
  );

  // Le parole si correggono DENTRO la bolla, al posto del messaggio: Annulla la rimette com'era.
  function apriCorrezione(): void {
    const prima = Array.from(bolla.childNodes);
    const voce = r.kind === 'voice';
    const ta = el('textarea', {
      class: 'scrivi-testo', rows: '4', maxlength: '4000',
      'aria-label': voce ? `Le parole della tua risposta a ${caro}` : `Il tuo messaggio per ${caro}`,
    }) as HTMLTextAreaElement;
    ta.value = r.text ?? '';
    const esito = el('p', { role: 'status' }, '');
    const salva = el('button', { class: 'btn-verde', 'data-ruolo': 'salva-correzione' }, 'Salva la correzione ✓') as HTMLButtonElement;
    const chiudi = (): void => { bolla.replaceChildren(...prima); correggi?.focus(); };
    salva.addEventListener('click', async () => {
      const text = ta.value.trim();
      if (!text) { esito.className = 'avviso'; esito.textContent = 'Scrivi qualcosa: il messaggio non può restare vuoto.'; ta.focus(); return; }
      if (text === (r.text ?? '').trim()) { chiudi(); return; }
      salva.disabled = true;
      try {
        const nuova = await api.put<Reazione>(`/api/reactions/${r.id}`, { text });
        const rifatta = bollaFilo(nuova, ricarica, `Corretto ✓ ${caro} vedrà il messaggio nuovo.`);
        bolla.replaceWith(rifatta);
        rifatta.querySelector<HTMLButtonElement>('[data-ruolo="correggi-messaggio"]')?.focus();
      } catch (err) {
        // 404: la storia è finita nel cestino (da un altro dispositivo) o il messaggio non c'è
        // più; 403: chi usa il quaderno non è lei (il cookie admin). Lì riprovare non serve, e il
        // pulsante resta spento.
        const status = err instanceof ApiError ? err.status : 0;
        esito.className = 'avviso';
        esito.textContent = status === 404 ? 'Non trovo più questo messaggio: forse la storia è stata messa via.'
          : status === 403 ? 'Questo messaggio può correggerlo solo chi l’ha scritto.'
          : 'Non riesco a correggerlo adesso: riprova tra un momento.';
        salva.disabled = status === 403 || status === 404;
      }
    });
    bolla.replaceChildren(
      el('p', { class: 'filo-meta' }, voce ? 'Correggi le parole della tua risposta: la tua voce resta com’è.' : 'Correggi il tuo messaggio:'),
      ta,
      r.seen_by_caro ? el('p', { class: 'filo-meta' }, `${caro} l’ha già letto: vedrà che l’hai corretto.`) : '',
      salva,
      el('button', { class: 'btn-quieto', click: chiudi }, 'Annulla'),
      esito,
    );
    ta.focus();
  }
  return bolla;
}

// Avviso in cima allo schermo quando arriva una novità mentre l'app è aperta
// (controllo leggero ogni 3 minuti e al ritorno in primo piano).
function mostraAvvisoNovita(n: Novita): void {
  document.querySelector('.avviso-novita')?.remove();
  const box = el('div', { class: 'avviso-novita', role: 'status' },
    el('span', { class: 'novita-icona', 'aria-hidden': 'true' }, iconaNovita(n)),
    el('span', { class: 'avviso-novita-testo' }, fraseNovita(n, true)),
    el('button', { class: 'btn-mini', click: () => { box.remove(); location.hash = '#novita'; } }, 'Guarda'),
    el('button', { class: 'btn-mini chiudi', 'aria-label': 'Chiudi l’avviso', click: () => box.remove() }, '✕'),
  );
  document.body.append(box);
  setTimeout(() => box.remove(), 40_000);
}
async function controllaNovita(): Promise<void> {
  if (document.visibilityState !== 'visible') return;
  try {
    const nov = await api.get<{ count: number; items: Novita[]; ornamenti?: Ornamenti }>('/api/novita');
    const route = (location.hash || '#home').slice(1).split('?')[0]?.split('/')[0] ?? 'home';
    // Spegnere l'album da lontano deve arrivare ovunque, non solo in Casa e in Impostazioni
    // (le uniche due schermate che chiedono /api/state). MA non mentre lei sta registrando:
    // ridisegnare lì le porterebbe via il racconto che sta dettando.
    if (applicaOrnamenti(nov.ornamenti) && route !== 'registra') render();
    if (novitaViste >= 0 && nov.count > novitaViste && !['novita', 'posta', 'registra'].includes(route) && nov.items[0]) {
      mostraAvvisoNovita(nov.items[0]);
    }
    novitaViste = nov.count;
    aggiornaPallino(nov.count);
  } catch { /* offline o non collegata: pazienza */ }
}
setInterval(() => void controllaNovita(), 3 * 60_000);
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void controllaNovita(); });

/** Impostazioni: avvisi push sul tablet (funzionano solo dall'app installata sulla Home). */
const PUSH_MAMMA: PushEndpoints = { stato: '/api/push/vapid', subscribe: '/api/push/subscribe', unsubscribe: '/api/push/unsubscribe' };
function cartaAvvisi(statoEl: HTMLElement): HTMLElement {
  const stato = el('p', { role: 'status' }, '');
  const btnOn = el('button', { class: 'btn-secondario' }, '🔔  Attiva gli avvisi su questo tablet') as HTMLButtonElement;
  const btnOff = el('button', { class: 'btn-quieto' }, 'Spegni gli avvisi') as HTMLButtonElement;
  const supporto = supportoPush();
  const installata = appInstallata();

  async function aggiorna(): Promise<void> {
    btnOn.hidden = true; btnOff.hidden = true;
    if (!supporto) {
      stato.textContent = installata
        ? 'Su questo tablet gli avvisi non sono disponibili.'
        : 'Per ricevere gli avvisi, apri Memorie dall’icona sulla schermata Home del tablet (non da Safari).';
      return;
    }
    const s = await statoPush('/');
    if (s === 'attivo') {
      stato.textContent = `Avvisi attivi ✓ Quando ${caro} ti lascia un cuore o un messaggio, il tablet te lo dice.`;
      btnOff.hidden = false;
    } else if (s === 'bloccato') {
      stato.textContent = 'Gli avvisi sono bloccati nelle impostazioni del tablet (Impostazioni → Notifiche → Memorie).';
    } else if (s === 'errore') {
      stato.textContent = 'Non riesco a controllare gli avvisi in questo momento.';
    } else {
      stato.textContent = `Vuoi che il tablet ti avvisi quando ${caro} ti scrive o ti lascia un cuore?`;
      btnOn.hidden = false;
    }
  }
  btnOn.addEventListener('click', async () => {
    btnOn.disabled = true;
    try {
      // attivaPush chiede il permesso come primo await: nel click non deve venire nulla prima.
      if (await attivaPush(api, PUSH_MAMMA, '/') === 'attivo') statoEl.textContent = 'Fatto: gli avvisi sono attivi ✓';
    } catch {
      statoEl.textContent = 'Non sono riuscita ad attivare gli avvisi: riprova più tardi.';
    }
    btnOn.disabled = false;
    await aggiorna();
  });
  btnOff.addEventListener('click', async () => {
    try {
      await spegniPush(api, PUSH_MAMMA, '/');
      statoEl.textContent = 'Va bene: niente più avvisi su questo tablet ✓';
    } catch { /* ok */ }
    await aggiorna();
  });
  void aggiorna();
  return el('div', { class: 'carta' }, el('p', { class: 'etichetta' }, `🔔 Avvisi da ${caro}`), stato, btnOn, btnOff);
}

/* ---------- spunti di conversazione ---------- */

type Spunto = {
  id: number; text: string; fromSon: boolean; personalized: boolean; mine?: boolean;
  voiceUrl: string | null; personName: string | null;
  theme: { slug: string; title: string; emoji: string | null; color: string | null } | null;
};

async function renderSpunti(): Promise<void> {
  const spunti = await api.get<Spunto[]>('/api/questions/browse');
  const pezzi: Array<Node> = [testata('Spunti di conversazione')];
  pezzi.push(el('p', { class: 'data-oggi mb-3' },
    'Tocca una domanda che ti ispira e racconta. Con ✏️ la cambi, con 🗑 la togli: gli spunti sono tuoi.'));

  // «Aggiungi un tuo spunto»: un argomento di cui LEI vuole parlare, prima o poi.
  const areaNuovo = el('div', { class: 'mb-4' });
  const mostraAggiungi = (): void => {
    areaNuovo.replaceChildren(
      el('button', { class: 'btn-secondario', click: () => {
        const inp = el('input', { class: 'scrivi-testo', placeholder: 'Di cosa vorresti raccontare? Scrivilo qui…', 'aria-label': 'Nuovo spunto' }) as HTMLInputElement;
        areaNuovo.replaceChildren(
          el('div', { class: 'scrivi-area' }, inp,
            el('button', { class: 'btn-verde', click: async () => {
              const t = inp.value.trim();
              if (t.length < 5) return;
              try { await api.post('/api/questions', { text: t }); render(); } catch { mostraAggiungi(); }
            } }, 'Aggiungi ✓')),
          el('button', { class: 'btn-quieto', click: mostraAggiungi }, 'Annulla'),
        );
        inp.focus();
      } }, '➕  Aggiungi un tuo spunto'),
    );
  };
  mostraAggiungi();
  pezzi.push(areaNuovo);

  if (spunti.length === 0) {
    pezzi.push(el('p', { class: 'vuoto' }, 'Per oggi le domande sono finite: torna domani, o racconta quello che vuoi dalla pagina iniziale.'));
  } else {
    const gruppi = new Map<string, { title: string; emoji: string; items: Spunto[] }>();
    for (const sp of spunti) {
      const key = sp.theme?.slug ?? 'altre';
      if (!gruppi.has(key)) {
        gruppi.set(key, { title: sp.theme?.title ?? 'Domande pensate per te', emoji: sp.theme?.emoji ?? '💭', items: [] });
      }
      gruppi.get(key)!.items.push(sp);
    }
    for (const g of gruppi.values()) {
      pezzi.push(el('div', { class: 'spunti-sezione' }, el('span', { class: 'emoji' }, g.emoji), el('h3', {}, g.title)));
      for (const sp of g.items.slice(0, 6)) {
        const testoEl = el('span', {}, sp.text);
        const riga = el('div', { class: 'spunto-riga' });
        const strumenti = el('div', { class: 'spunto-strumenti' },
          el('button', { class: 'spunto-mini', 'aria-label': 'Cambia questo spunto', click: () => {
            const inp = el('input', { class: 'scrivi-testo', value: sp.text, 'aria-label': 'Testo dello spunto' }) as HTMLInputElement;
            riga.replaceChildren(
              el('div', { class: 'scrivi-area' }, inp,
                el('button', { class: 'btn-verde', click: async () => {
                  const t = inp.value.trim();
                  if (t.length < 5) return;
                  try { await api.put(`/api/questions/${sp.id}`, { text: t }); render(); } catch { render(); }
                } }, 'Salva ✓')),
              el('button', { class: 'btn-quieto', click: () => render() }, 'Annulla'),
            );
            inp.focus();
          } }, '✏️'),
          el('button', { class: 'spunto-mini', 'aria-label': 'Togli questo spunto', click: async () => {
            try {
              await api.post(`/api/questions/${sp.id}/ritira`);
              riga.replaceChildren(
                el('div', { class: 'spunto spunto-tolto' }, el('span', {}, 'Tolto ✓ '),
                  el('button', { class: 'btn-quieto', click: async () => { await api.post(`/api/questions/${sp.id}/ripristina`); render(); } }, '↩️ Rimettilo')),
              );
            } catch { render(); }
          } }, '🗑'),
        );
        riga.append(
          el('button', { class: 'spunto', click: () => { location.hash = `#registra?mode=dialogo&q=${sp.id}`; } },
            sp.fromSon
              ? el('span', { class: 'badge badge-vincenzo' }, `Da ${caro}`)
              : sp.mine
                ? el('span', { class: 'badge badge-personale' }, 'Il tuo spunto')
                : sp.personalized
                  ? el('span', { class: 'badge badge-personale' }, 'Pensata per te')
                  : null,
            testoEl,
          ),
          strumenti,
        );
        pezzi.push(riga);
      }
    }
  }
  screen(...pezzi);
}

/* ---------- ricettario ---------- */

type RicettaCard = MemoryCard & { diChi?: string | null; tipo?: 'dolce' | 'salato' | null };

async function renderRicette(): Promise<void> {
  const ricette = await api.get<RicettaCard[]>('/api/ricette');
  const pezzi: Array<Node> = [testata('Il ricettario')];
  pezzi.push(
    el('button', { class: 'btn-primario mb-4', 'data-surprise-anchor': 'ricette', click: () => { location.hash = '#registra?mode=dialogo&ricetta=1'; } },
      '🍳  Aggiungi una ricetta'),
  );
  if (ricette.length === 0) {
    pezzi.push(el('p', { class: 'vuoto' },
      'Qui nasce il ricettario di casa: detta una ricetta come la racconteresti in cucina — ingredienti, procedimento e il tuo segreto.'));
    screen(...pezzi);
    return;
  }

  // Le due voci che ha chiesto mamma. Chi non ha ancora un posto sta in fondo, con il tocco per
  // darglielo: meglio una ricetta in disparte che una torta in mezzo agli arrosti.
  const riga = (r: RicettaCard): HTMLElement =>
    el('button', { class: 'storia-riga', click: () => { location.hash = `#storia/${r.id}`; } },
      r.photo ? el('img', { class: 'crono-thumb', src: `/api/media/${r.photo}?w=${LARGHEZZA_GRIGLIA}`, alt: '', loading: 'lazy', decoding: 'async' }) : el('span', { class: 'icona-riga', 'aria-hidden': 'true' }, '🍲'),
      el('div', {},
        el('div', {}, r.title),
        el('div', { class: 'quando' }, r.diChi ? `ricetta ${r.diChi}` : `dettata il ${fmtDate(r.toldAt)}`),
      ),
    );

  // Le due voci ci sono sempre e due, anche quando una è vuota: se mamma ha dettato solo dolci
  // e vedesse una sezione sola, penserebbe che non è cambiato niente — e invece è cambiato, e
  // l'altra la sta aspettando.
  const sezione = (titolo: string, emoji: string, lista: RicettaCard[], invito: string): HTMLElement =>
    el('section', { class: 'ricette-sezione' },
      el('h2', { class: 'sezione' }, `${emoji}  ${titolo}`),
      ...(lista.length > 0 ? lista.map(riga) : [el('p', { class: 'didascalia' }, invito)]),
    );

  const daMettere = ricette.filter((r) => r.tipo !== 'dolce' && r.tipo !== 'salato');
  pezzi.push(
    sezione('Dolci', '🍰', ricette.filter((r) => r.tipo === 'dolce'),
      'Qui andranno le torte, i biscotti e le creme: la prima che detti la trovi qui.'),
    sezione('Salati', '🍲', ricette.filter((r) => r.tipo === 'salato'),
      'Qui andranno i primi, i sughi e gli arrosti: la prima che detti la trovi qui.'),
  );

  if (daMettere.length > 0) {
    const box = el('section', { class: 'ricette-sezione' },
      el('h2', { class: 'sezione' }, '🧺  Da mettere in ordine'),
      el('p', { class: 'didascalia' }, 'Dimmi tu dove vanno: un tocco e le trovi sempre al posto giusto.'),
    );
    for (const r of daMettere) {
      const esito = el('p', { class: 'didascalia', role: 'status' }, '');
      const scegli = (tipo: 'dolce' | 'salato') =>
        el('button', { class: 'btn-secondario', click: async (ev: Event) => {
          const btn = ev.currentTarget as HTMLButtonElement;
          btn.disabled = true;
          try {
            await api.post(`/api/ricette/${r.id}/tipo`, { tipo });
            esito.textContent = tipo === 'dolce' ? 'Messa fra i dolci ✓' : 'Messa fra i salati ✓';
            // Si ridisegna dopo un attimo, così fa in tempo a leggere che è andata.
            window.setTimeout(() => { if (esito.isConnected) void renderRicette(); }, 900);
          } catch {
            btn.disabled = false;
            esito.textContent = 'Non riesco a spostarla adesso: riprova tra poco.';
          }
        } }, tipo === 'dolce' ? '🍰  È un dolce' : '🍲  È un salato');
      box.append(el('div', { class: 'carta ricetta-scelta' },
        el('p', { class: 'domanda-testo' }, r.title),
        el('div', { class: 'griglia-due' }, scegli('dolce'), scegli('salato')),
        esito,
      ));
    }
    pezzi.push(box);
  }
  screen(...pezzi);
}

/* ---------- cronologia ---------- */

type TimelineItem = { kind: 'storia' | 'foto'; year: number | null; dateText: string | null; dateFrom?: 'foto'; id: number; title: string; memoryId?: number | null; photo?: number | null; hasVideo?: boolean };

/** Il segnale di una storia che ha un video pronto: piccolo, accanto alla data. */
function segnoVideo(): HTMLElement {
  return el('span', { class: 'segno-video' }, el('span', { 'aria-hidden': 'true' }, ' · 🎞'), ' con un video');
}

async function renderCronologia(): Promise<void> {
  const t = await api.get<{ dated: TimelineItem[]; undated: TimelineItem[]; decenniSfogliabili?: number[] }>('/api/timeline');
  const pezzi: Array<Node> = [testata('La tua storia nel tempo')];
  const vaiA = (item: TimelineItem) => {
    if (item.kind === 'storia') location.hash = `#storia/${item.id}`;
    else if (item.memoryId) location.hash = `#storia/${item.memoryId}`;
  };
  const rigaDi = (item: TimelineItem): HTMLElement =>
    el('button', { class: 'storia-riga', click: () => vaiA(item) },
      item.kind === 'foto' || item.photo
        ? el('img', { class: 'crono-thumb', src: `/api/media/${item.kind === 'foto' ? item.id : item.photo}?w=${LARGHEZZA_GRIGLIA}`, alt: '', loading: 'lazy', decoding: 'async' })
        : el('span', { class: 'icona-riga', 'aria-hidden': 'true' }, '📖'),
      el('div', {},
        el('div', {}, item.title),
        el('div', { class: 'quando' },
          item.dateText
            ? (item.dateFrom === 'foto' ? `dalla foto: ${fmtDate(item.dateText)}` : fmtDate(item.dateText))
            : (item.year !== null ? String(item.year) : 'senza data, per ora'),
          item.hasVideo ? segnoVideo() : null),
      ),
    );

  if (t.dated.length === 0 && t.undated.length === 0) {
    pezzi.push(el('p', { class: 'vuoto' },
      'Quando i racconti diranno «era il 1962…», qui prenderà forma la linea della tua vita, anno per anno, con storie e fotografie.'));
  } else {
    let decadeCorrente = -1;
    for (const item of t.dated) {
      const dec = Math.floor((item.year ?? 0) / 10) * 10;
      if (dec !== decadeCorrente) {
        decadeCorrente = dec;
        pezzi.push(el('div', { class: 'spunti-sezione' }, el('span', { class: 'emoji' }, '🕰️'), el('h3', {}, `Anni ${dec}`)));
        // Con l'album, un decennio è una raccolta da sfogliare — ma solo se ha dei RACCONTI:
        // le intestazioni della cronologia nascono anche dalle sole fotografie sciolte, e un
        // album di decennio senza storie è un 404, cioè una schermata d'errore in faccia a lei.
        const anni = dec;
        if ((t.decenniSfogliabili ?? []).includes(anni)) {
          pezzi.push(el('button', { class: 'album-decennio', click: () => { location.hash = `#album/decennio/${anni}`; } },
            el('span', {}, `📖  Sfoglia gli anni ${anni}`)));
        }
      }
      pezzi.push(rigaDi(item));
    }
    // Il nome di questa sezione l'ha scelto mamma: «Varie negli anni» 😊
    if (t.undated.length > 0) {
      pezzi.push(el('div', { class: 'spunti-sezione' }, el('span', { class: 'emoji' }, '🗂️'), el('h3', {}, 'Varie negli anni')));
      for (const item of t.undated) pezzi.push(rigaDi(item));
      pezzi.push(el('p', { class: 'data-oggi mt-3' },
        'Questi ricordi non hanno ancora un anno: raccontando «quando» è successo, saliranno al loro posto nella linea del tempo.'));
    }
  }
  screen(...pezzi);
}

/* ---------- fotografie ---------- */

type FotoCard = { id: number; caption: string | null; memory_id: number | null; memory_title: string | null; story_id?: number | null; story_title?: string | null; created_at?: string; captured_edtf?: string | null; narrated?: number; in_libreria?: number; guest_id?: number | null;
  /** Dalla pagina di una storia: il posto (1 = la prima) e quante foto ha, solo le sue. Senza, niente «un posto prima/dopo». */
  posto?: number; totale?: number };
/** Le foto accanto a questa, nell'ordine che mamma ha davanti. È una funzione e non un elenco
 *  perché la griglia può essere rifatta sotto il dialogo (eliminando o spostando una foto):
 *  rileggendo il DOM vivo al momento del tocco, «prima/dopo» non punta mai a una foto sparita. */
type SfogliaFoto = { lista: () => FotoCard[]; nodo: (f: FotoCard) => HTMLElement | null; daStoria?: boolean };

/** «Scattata il…» si dice solo quando è vero. In archivio metà delle fotografie porta come data
 *  di scatto il giorno in cui mamma le ha caricate (le foto di famiglia sono scansioni, e l'EXIF
 *  si perde nella normalizzazione): dire «scattata il 14 marzo 2026» di una foto del 1964 sarebbe
 *  una bugia. Se l'anno coincide con quello del caricamento, si tace e resta «Aggiunta il…». */
function dataDelloScatto(f: FotoCard): string | null {
  const scatto = f.captured_edtf?.trim();
  if (!scatto) return null;
  const anno = annoIniziale(scatto);
  const annoCaricamento = f.created_at ? /^(\d{4})/.exec(f.created_at)?.[1] : undefined;
  if (anno === null || String(anno) === annoCaricamento) return null;
  // «Scattata il 12 giugno 1964», «nel giugno 1964», «negli anni Cinquanta»: mai «il giugno».
  const testo = nellaData(scatto);
  return testo ? `Scattata ${testo}` : null; // una stringa che non sappiamo leggere: si tace
}

function apriFotoPiena(f: FotoCard, trigger: HTMLElement | null, sfoglia?: SfogliaFoto, fuoco: 'chiudi' | 'avanti' | 'indietro' = 'chiudi'): void {
  const background = [app, familyNavigation].map((node) => ({ node, inert: node.inert }));
  const previousOverflow = document.body.style.overflow;
  let closed = false;
  const chiudi = () => {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('hashchange', chiudi);
    overlay.remove();
    for (const { node, inert } of background) node.inert = inert;
    document.body.style.overflow = previousOverflow;
    if (trigger?.isConnected) trigger.focus();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); chiudi(); }
    if (e.key !== 'Tab') return;
    const controls = [...overlay.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex="0"]')]
      .filter((node) => node.getClientRects().length > 0);
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
  };

  const scatto = dataDelloScatto(f);
  const didaEl = el('p', { class: 'didascalia' },
    f.caption ?? f.story_title ?? '',
    scatto ? el('span', { class: 'didascalia-data' }, scatto) : null,
    f.created_at ? el('span', { class: 'didascalia-data' }, `Aggiunta il ${fmtDate(f.created_at)}`) : null,
  );
  const esitoEl = el('p', { class: 'didascalia', role: 'status' }, '');
  const azioni = el('div', { class: 'griglia-azioni' });
  const btnChiudi = el('button', { click: chiudi }, 'Chiudi');

  // Didascalia: si salva da sola quando esce dal campo (niente più testi persi).
  let editorAperto: HTMLElement | null = null;
  async function salvaDidascalia(inp: HTMLInputElement): Promise<void> {
    const v = inp.value.trim();
    if ((f.caption ?? '') === v) return;
    try {
      await api.put(`/api/media/${f.id}/caption`, { caption: v });
      f.caption = v || null;
      didaEl.firstChild!.textContent = f.caption ?? f.story_title ?? '';
      esitoEl.textContent = 'Didascalia salvata ✓';
    } catch {
      esitoEl.textContent = 'Non riesco a salvare la didascalia: riprova.';
    }
  }
  function apriEditorDidascalia(): void {
    if (editorAperto) return;
    const inp = el('input', { class: 'scrivi-testo', value: f.caption ?? '', placeholder: 'Scrivi la didascalia…', 'aria-label': 'Didascalia' }) as HTMLInputElement;
    const salva = el('button', { class: 'btn-verde' }, 'Salva ✓') as HTMLButtonElement;
    inp.addEventListener('blur', () => void salvaDidascalia(inp));
    salva.addEventListener('click', async () => {
      await salvaDidascalia(inp);
      editorAperto?.remove();
      editorAperto = null;
    });
    editorAperto = el('div', { class: 'scrivi-area' }, inp, salva);
    overlay.insertBefore(editorAperto, azioni);
    inp.focus();
    inp.scrollIntoView({ block: 'center' });
  }

  function mostraAzioni(): void {
    const bottoni: Array<Node> = [];
    if (f.story_id) {
      if (location.hash !== `#storia/${f.story_id}`) {
        bottoni.push(el('button', { class: 'btn-racconta-foto', click: () => { chiudi(); location.hash = `#storia/${f.story_id}`; } },
          `📖  Vai al racconto${f.story_title ? ` «${f.story_title}»` : ''}`));
      }
      if (f.memory_id) {
        // Il posto dentro la storia: un passo alla volta, niente trascinamenti. Solo dalla pagina
        // della storia (è lì che si sa il posto) e solo con almeno due foto.
        if (f.posto && f.totale && f.totale > 1) {
          const prima = el('button', { 'data-ruolo': 'posto-prima', click: () => void muoviSubito(f.posto! - 1, 'posto-prima') }, '⬆️  Mettila un posto prima') as HTMLButtonElement;
          const dopo = el('button', { 'data-ruolo': 'posto-dopo', click: () => void muoviSubito(f.posto! + 1, 'posto-dopo') }, '⬇️  Mettila un posto dopo') as HTMLButtonElement;
          prima.disabled = f.posto <= 1 || spostando;
          dopo.disabled = f.posto >= f.totale || spostando;
          if (rimettiAl !== null) bottoni.push(el('button', { class: 'btn-verde', 'data-ruolo': 'posto-annulla', click: () => void muoviSubito(rimettiAl!, 'posto-annulla') }, '↩️  Rimettila dov\'era'));
          bottoni.push(prima, dopo);
        }
        bottoni.push(el('button', { click: () => void apriSelettoreStoria(true) }, '🔀  Spostala in un\'altra storia'));
        bottoni.push(el('button', { click: () => void staccaSubito() }, '📤  Toglila da questa storia'));
      }
    } else {
      bottoni.push(el('button', { class: 'btn-racconta-foto', click: () => void apriSelettoreStoria(false) }, '➕  Mettila in una storia'));
      bottoni.push(el('button', { click: () => { chiudi(); location.hash = `#registra?mode=ascolto&foto=${f.id}`; } }, '🎙️  Raccontamela (racconto nuovo)'));
    }
    // Una fotografia portata da un parente: è già nel suo racconto, ma nell'album di mamma
    // ci entra solo se lo dice lei.
    if (f.in_libreria === 0) {
      bottoni.push(el('button', { class: 'btn-racconta-foto', click: async () => {
        try {
          await api.post(`/api/media/${f.id}/tieni`);
          f.in_libreria = 1;
          esitoEl.textContent = 'Eccola, adesso è anche nel tuo album ✓';
        } catch {
          esitoEl.textContent = 'Non sono riuscita a tenerla: riprova.';
        }
        aggiornaDialogo();
      } }, '💚  Tienila nel mio album'));
    }
    // Arrivata dall'albero per il ritratto di qualcuno: quella è la prima cosa da fare.
    if (ritrattoPer) bottoni.unshift(el('button', { class: 'btn-racconta-foto', click: () => ritaglia(ritrattoPer!) }, `👤  Usala per il ritratto di ${ritrattoPer.name}`));
    // Dentro una storia no (#33, chiesto da lei): il ritratto si sceglie da «Le fotografie» o dall'albero.
    // Non basta `story_id`: anche una foto della galleria può stare in una storia.
    else if (!sfoglia?.daStoria) bottoni.push(el('button', { click: () => void scegliPersona() }, '👤  È il ritratto di…'));
    bottoni.push(el('button', { click: apriEditorDidascalia }, '✏️  Didascalia'));
    bottoni.push(el('button', { click: () => { chiudi(); location.hash = `#chiedi?foto=${f.id}`; } }, '💬  Dillo al quaderno'));
    bottoni.push(el('button', { click: () => void eliminaSubito() }, '🗑  Elimina questa foto'));
    azioni.replaceChildren(...bottoni);
  }

  // Selettore e conferme appaiono AL POSTO dei pulsanti (dove ha appena toccato),
  // mai più in basso fuori dallo schermo.
  // Il ritratto: di chi è il volto, poi il ritaglio nel cerchio del medaglione.
  async function scegliPersona(): Promise<void> {
    esitoEl.textContent = '';
    const norm = (t: string) => t.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    let persone: PersonaRitratto[];
    try {
      const atlas = await api.get<{ nodes: PersonaRitratto[] }>('/api/family/atlas');
      persone = atlas.nodes.filter((p) => p.id > 0 && p.status === 'confirmed').sort((a, b) => a.name.localeCompare(b.name, 'it'));
    } catch {
      esitoEl.textContent = 'Non riesco a leggere le persone della famiglia: riprova tra poco.';
      return;
    }
    const cerca = el('input', { type: 'search', class: 'input', placeholder: 'Un nome…', 'aria-label': 'Cerca la persona' }) as HTMLInputElement;
    const lista = el('div', { class: 'lista-persone' });
    const aggiorna = (): void => {
      const q = norm(cerca.value.trim());
      const trovate = q ? persone.filter((p) => norm(p.name).includes(q)) : persone;
      lista.replaceChildren(...trovate.map((p) => el('button', { class: 'riga-persona', click: () => ritaglia(p) },
        el('span', {}, p.name), p.relation ? el('span', { class: 'quando' }, p.relation) : null)));
      if (!trovate.length) lista.append(el('p', { class: 'didascalia' }, 'Nessuna persona con questo nome.'));
    };
    cerca.addEventListener('input', aggiorna);
    aggiorna();
    azioni.replaceChildren(el('div', { class: 'selettore-storie' },
      el('p', { class: 'didascalia' }, 'Di chi è il volto in questa foto?'), cerca,
      el('button', { class: 'btn-quieto', click: aggiornaDialogo }, 'Annulla'), lista));
    azioni.scrollIntoView({ block: 'center' });
  }

  function ritaglia(p: { id: number; name: string }): void {
    esitoEl.textContent = '';
    const editor = editorRitaglio(`/api/media/${f.id}?w=${LARGHEZZA_LASTRA}`, f.caption ?? 'Fotografia');
    const salva = el('button', { class: 'btn-verde', click: async () => {
      salva.disabled = true;
      try {
        const r = await api.post<{ mediaId: number; precedente: number | null }>(`/api/family/people/${p.id}/ritratto`, { mediaId: f.id, ritaglio: editor.ritaglio() });
        esitoEl.textContent = `Ora ${p.name} ha il suo ritratto nell’albero ✓`;
        azioni.replaceChildren(
          el('button', { class: 'btn-verde', click: () => { chiudi(); location.hash = `#albero?persona=${p.id}`; } }, '🌳  Guardalo nell’albero'),
          el('button', { class: 'btn-quieto', click: async () => {
            try {
              await api.post(`/api/family/people/${p.id}/ritratto/annulla`, { mediaId: r.mediaId, precedente: r.precedente });
              esitoEl.textContent = 'Fatto: il ritratto di prima è tornato al suo posto.';
            } catch { esitoEl.textContent = 'Non sono riuscita ad annullare: chiedi a ' + caro + '.'; }
            aggiornaDialogo();
          } }, '↩️  Annulla'),
        );
      } catch {
        esitoEl.textContent = 'Non sono riuscita a fare il ritratto: riprova.';
        salva.disabled = false;
      }
    } }, '✓  Salva il ritratto') as HTMLButtonElement;
    azioni.replaceChildren(el('div', { class: 'ritaglio-passo' },
      el('p', { class: 'didascalia' }, `Metti il volto di ${p.name} nel cerchio.`),
      editor.element, salva,
      el('button', { class: 'btn-quieto', click: aggiornaDialogo }, 'Annulla')));
    azioni.scrollIntoView({ block: 'center' });
  }

  async function apriSelettoreStoria(sposta: boolean): Promise<void> {
    esitoEl.textContent = '';
    azioni.replaceChildren(el('p', { class: 'didascalia' }, 'Un momento…'));
    const storie = await api.get<Array<{ id: number; title: string; told_at: string }>>('/api/memories/recent').catch(() => []);
    const disponibili = storie.filter((st) => st.id !== f.memory_id);
    if (disponibili.length === 0) {
      esitoEl.textContent = 'Non ci sono ancora altre storie a cui aggiungerla.';
      aggiornaDialogo();
      return;
    }
    const norm = (t: string) => t.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const cerca = el('input', { class: 'scrivi-testo', placeholder: 'Cerca per titolo…', 'aria-label': 'Cerca una storia' }) as HTMLInputElement;
    const lista = el('div', { class: 'selettore-lista' });
    const rigaStoria = (st: { id: number; title: string; told_at: string }): HTMLElement =>
      el('button', { class: 'storia-riga', click: async () => {
        try {
          listaCambiata = true;
          await api.post(`/api/media/${f.id}/attach`, { memoryId: st.id, move: sposta || undefined });
          f.memory_id = st.id;
          f.story_id = st.id;
          f.story_title = st.title;
          esitoEl.textContent = `${sposta ? 'Spostata' : 'Aggiunta'} a «${st.title}» ✓`;
          aggiornaDialogo();
          render();
        } catch {
          esitoEl.textContent = 'Non ci sono riuscita: riprova.';
          aggiornaDialogo();
        }
      } },
        el('div', {}, el('div', {}, st.title), el('div', { class: 'quando' }, `raccontata il ${fmtDate(st.told_at)}`)),
      );
    const aggiorna = (): void => {
      const q = norm(cerca.value.trim());
      const filtrate = q ? disponibili.filter((st) => norm(st.title).includes(q)) : disponibili;
      lista.replaceChildren(...filtrate.map(rigaStoria));
      if (filtrate.length === 0) lista.append(el('p', { class: 'didascalia' }, 'Nessuna storia con questo titolo.'));
    };
    cerca.addEventListener('input', aggiorna);
    aggiorna();
    azioni.replaceChildren(
      el('div', { class: 'selettore-storie' },
        el('p', { class: 'didascalia' }, sposta ? 'In quale storia vuoi spostarla?' : 'A quale storia vuoi aggiungerla?'),
        cerca,
        el('button', { class: 'btn-quieto', click: aggiornaDialogo }, 'Annulla'),
        lista,
      ),
    );
    azioni.scrollIntoView({ block: 'center' });
  }

  // Niente domande di conferma: il tocco fa subito la cosa, e al suo posto
  // compare un solo pulsante per annullare. (Le conferme le perdeva per strada.)
  async function staccaSubito(): Promise<void> {
    esitoEl.textContent = '';
    const storiaDiPrima = f.memory_id;
    const titoloDiPrima = f.story_title;
    try {
      listaCambiata = true;
      await api.post(`/api/media/${f.id}/detach`);
      f.memory_id = null;
      f.story_id = null;
      f.story_title = null;
      esitoEl.textContent = 'Tolta dalla storia ✓ Resta nell\'album delle fotografie.';
      azioni.replaceChildren(
        el('button', { class: 'btn-verde', click: async () => {
          try {
            await api.post(`/api/media/${f.id}/attach`, { memoryId: storiaDiPrima });
            f.memory_id = storiaDiPrima;
            f.story_id = storiaDiPrima;
            f.story_title = titoloDiPrima;
            esitoEl.textContent = 'Rimessa nella storia ✓';
          } catch {
            esitoEl.textContent = 'Non sono riuscita a rimetterla: riprova.';
          }
          aggiornaDialogo();
          render();
        } }, '↩️  Rimettila nella storia'),
      );
      render();
    } catch {
      esitoEl.textContent = 'Non ci sono riuscita: riprova.';
      aggiornaDialogo();
    }
  }

  // «Mettila un posto prima/dopo»: come staccaSubito, il tocco fa subito la cosa e resta un solo
  // gesto per ripensarci. A differenza di quello i pulsanti restano (spostarla di due posti sono
  // due tocchi): «Rimettila dov'era» rimette il posto di PRIMA dell'ultimo spostamento. Il fuoco
  // non si perde mai: resta sul pulsante appena toccato, o — se la foto è arrivata in fondo e quel
  // pulsante si è spento — passa a «Rimettila dov'era».
  let spostando = false;
  let rimettiAl: number | null = null;
  async function muoviSubito(posto: number, ruolo: string): Promise<void> {
    const annulla = ruolo === 'posto-annulla';
    if (spostando) return;
    esitoEl.textContent = '';
    spostando = true;
    try {
      const r = await api.post<{ da: number; posto: number; totale: number }>(`/api/media/${f.id}/posto`, { posto });
      listaCambiata = true; // la pagina sotto si rifà in un altro ordine: lo sfoglio non sa più dove sta
      f.posto = r.posto;
      f.totale = r.totale;
      rimettiAl = annulla ? null : r.da;
      esitoEl.textContent = annulla
        ? `Rimessa dov'era ✓ Ora è la fotografia ${r.posto} di ${r.totale}.`
        : `Spostata ✓ Ora è la fotografia ${r.posto} di ${r.totale}.`;
      render();
    } catch {
      esitoEl.textContent = 'Non ci sono riuscita: riprova.';
    }
    spostando = false;
    aggiornaDialogo();
    const ancora = (r: string): HTMLButtonElement | null => azioni.querySelector<HTMLButtonElement>(`[data-ruolo="${r}"]:not(:disabled)`);
    (ancora(ruolo) ?? ancora('posto-annulla') ?? ancora('posto-prima') ?? ancora('posto-dopo') ?? btnChiudi).focus();
  }

  async function eliminaSubito(): Promise<void> {
    esitoEl.textContent = '';
    try {
      await api.post(`/api/media/${f.id}/hide`);
      listaCambiata = true;
      esitoEl.textContent = 'Foto tolta ✓ Se ci hai ripensato, riprendila qui sotto.';
      azioni.replaceChildren(
        el('button', { class: 'btn-verde', click: async () => {
          try {
            await api.post(`/api/media/${f.id}/unhide`);
            esitoEl.textContent = 'Eccola di nuovo qui ✓';
          } catch {
            esitoEl.textContent = 'Non sono riuscita a riprenderla: chiedi a ' + caro + '.';
          }
          aggiornaDialogo();
          render();
        } }, '↩️  Riprendila'),
      );
      // Qui non si può usare aggiornaDialogo(): cancellerebbe il «Riprendila» appena messo.
      // Lo sfoglio però va ridisegnato lo stesso, o continuerebbe a dire «5 di 124».
      disegnaSfoglia();
      render();
    } catch (err) {
      esitoEl.textContent = err instanceof ApiError && err.status === 400
        ? 'Questa foto è il ritratto di una persona: chiedi a ' + caro + ' per toglierla.'
        : 'Non sono riuscita a toglierla: riprova.';
      aggiornaDialogo();
    }
  }

  // Sfogliare le fotografie come le pagine dell'album: due pulsanti grandi, mai lo swipe
  // (sull'iPad lo scorrimento dal bordo è il «indietro» di Safari). L'ordine è quello che mamma
  // ha davanti nella griglia, filtri compresi: si rilegge al momento del tocco.
  const sfogliaEl = el('div', { class: 'foto-sfoglia' });
  // Toccando una foto (eliminata, staccata, spostata) il router rifà la galleria sotto il
  // dialogo, e da quel momento questo dialogo non sa più dire ONESTAMENTE dove si trova: il
  // conto sarebbe «5 di 124» con 123 foto. Invece di mentire, smette di sfogliare — le azioni
  // e il «Riprendila» restano lì, e chiudendo e riaprendo si riprende da dove si è.
  let listaCambiata = false;
  function disegnaSfoglia(): void {
    sfogliaEl.replaceChildren();
    if (listaCambiata || !sfoglia) return;
    const lista = sfoglia.lista();
    const i = lista.findIndex((x) => x.id === f.id);
    if (i < 0 || lista.length < 2) return;
    const passo = (verso: -1 | 1): void => {
      const prossima = lista[i + verso];
      if (!prossima) return;
      chiudi();
      apriFotoPiena(prossima, sfoglia!.nodo(prossima), sfoglia, verso > 0 ? 'avanti' : 'indietro');
    };
    sfogliaEl.append(
      i > 0
        ? el('button', { class: 'foto-passo', 'data-verso': 'indietro', click: () => passo(-1) }, '‹  Foto prima')
        : el('span', {}),
      i < lista.length - 1
        ? el('button', { class: 'foto-passo', 'data-verso': 'avanti', click: () => passo(1) }, 'Foto dopo  ›')
        : el('span', {}),
    );
    sfogliaEl.append(el('p', { class: 'foto-dove', role: 'status', 'aria-live': 'polite' }, `Fotografia ${i + 1} di ${lista.length}`));
    // La successiva si scalda in silenzio: è già in cache su disco, costa poco e la voltata è netta.
    const dopo = lista[i + 1];
    if (dopo) new Image().src = `/api/media/${dopo.id}?w=${LARGHEZZA_LASTRA}`;
  }

  /** Azioni e sfoglio si ridisegnano SEMPRE insieme: dopo un'eliminazione o uno spostamento il
   *  router rifà la griglia sotto il dialogo, e un elenco fotografato all'apertura direbbe
   *  «Fotografia 5 di 124» quando le foto sono 123. */
  function aggiornaDialogo(): void { mostraAzioni(); disegnaSfoglia(); }

  const overlay = el('div', { class: 'foto-piena', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Fotografia ingrandita' },
    el('img', {
      'data-ruolo': 'volto',
      src: `/api/media/${f.id}?w=${LARGHEZZA_LASTRA}`, alt: f.caption ?? 'Fotografia',
      click: () => overlay.classList.toggle('foto-grande'),
    }),
    didaEl,
    esitoEl,
    sfogliaEl,
    azioni,
    btnChiudi,
  );
  aggiornaDialogo();
  document.addEventListener('keydown', onKey);
  window.addEventListener('hashchange', chiudi);
  for (const { node } of background) node.inert = true;
  document.body.style.overflow = 'hidden';
  document.body.append(overlay);
  // Sfogliando, il fuoco resta sul pulsante della STESSA direzione: prendendo semplicemente il
  // primo `.foto-passo` si finiva su «‹ Foto prima» e con la tastiera si rimbalzava fra due foto.
  // Se in quella direzione non si può più andare (siamo in fondo), il fuoco torna su «Chiudi».
  const daMettereAFuoco = fuoco === 'chiudi' ? null : sfogliaEl.querySelector<HTMLElement>(`.foto-passo[data-verso="${fuoco}"]`);
  (daMettereAFuoco ?? btnChiudi).focus();
}

/** Carica foto normalizzate, con la data del file per la cronologia. */
async function caricaFoto(files: File[]): Promise<number[]> {
  const form = new FormData();
  const dates: Array<number | null> = [];
  for (const f of files.slice(0, 6)) {
    form.append('file', await normalizzaFoto(f), 'foto.jpg');
    dates.push(f.lastModified > 0 ? f.lastModified : null);
  }
  form.append('dates', JSON.stringify(dates));
  const res = await fetch('/api/photos/upload', { method: 'POST', body: form });
  if (!res.ok) throw new Error(String(res.status));
  return ((await res.json()) as { photoIds: number[] }).photoIds;
}

/** Ridimensiona e converte in JPEG lato client (gestisce anche gli HEIC dell'iPad). */
async function normalizzaFoto(file: File): Promise<Blob> {
  try {
    const bmp = await createImageBitmap(file);
    const scala = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bmp.width * scala));
    canvas.height = Math.max(1, Math.round(bmp.height * scala));
    canvas.getContext('2d')!.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.85));
    if (blob) return blob;
  } catch { /* formato non decodificabile qui: ci pensa il server */ }
  return file;
}

async function renderFoto(): Promise<void> {
  // I suoi video stanno in una riga a parte, sopra: non si mescolano alle fotografie. Senza
  // video (o con un server che non li conosce) la pagina è quella di sempre.
  const [foto, video] = await Promise.all([
    api.get<FotoCard[]>('/api/photos'),
    api.get<VideoElenco[]>('/api/video').catch((err: unknown) => {
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      return [] as VideoElenco[];
    }),
  ]);
  const pezzi: Array<Node> = [testata('Le fotografie')];
  if (ritrattoPer) {
    pezzi.push(el('div', { class: 'carta carta-ritratto', role: 'status' },
      el('p', { class: 'domanda-testo' }, `Scegli una foto con il volto di ${ritrattoPer.name}.`),
      el('p', {}, 'Toccala, poi «Usala per il ritratto»: il volto va nel medaglione dell’albero.'),
      el('a', { class: 'btn-quieto', href: '#foto' }, 'Non ora')));
  }
  const intro = el('section', { class: 'photo-intro', 'data-surprise-anchor': 'foto', 'aria-labelledby': 'photo-intro-title' },
    el('h2', { id: 'photo-intro-title' }, 'Ogni foto ha una storia.'),
    el('p', {}, 'Sfoglia il tuo album, oppure porta una fotografia e racconta chi c’era.'),
  );
  const gallery = el('section', { class: 'photo-gallery', 'aria-label': 'Album fotografico' });

  // Porta foto nuove e raccontale: si carica, poi si apre la chiacchierata
  // in cui l'app guarda le foto e fa le domande.
  const inputFoto = el('input', { type: 'file', accept: 'image/*', multiple: '', hidden: '' }) as HTMLInputElement;
  const btnPorta = el('button', { class: 'btn-primario mb-4' }, icon('plus'), 'Porta delle foto e raccontale') as HTMLButtonElement;
  const erroreCarico = el('p', { role: 'status' }, '');
  btnPorta.addEventListener('click', () => inputFoto.click());
  inputFoto.addEventListener('change', async () => {
    const files = Array.from(inputFoto.files ?? []).slice(0, 6);
    if (files.length === 0) return;
    btnPorta.disabled = true;
    btnPorta.textContent = 'Sto caricando le foto…';
    erroreCarico.textContent = '';
    erroreCarico.className = '';
    try {
      const photoIds = await caricaFoto(files);
      location.hash = `#registra?mode=dialogo&fotos=${photoIds.join(',')}`;
    } catch {
      btnPorta.disabled = false;
      btnPorta.textContent = '📷  Porta delle foto e raccontale';
      erroreCarico.className = 'avviso';
      erroreCarico.textContent = 'Non sono riuscita a caricare le foto: controlla la connessione e riprova.';
    }
  });
  intro.append(btnPorta, erroreCarico, inputFoto);

  // Mentre sceglie il ritratto di qualcuno, solo fotografie: i video la porterebbero altrove.
  const riga = ritrattoPer ? null : rigaVideo(video);
  if (riga) gallery.append(riga);
  if (foto.length === 0) {
    gallery.append(el('p', { class: 'vuoto' }, `Le foto che aggiungerai ai racconti (e quelle che porterà ${caro}) si raccoglieranno qui.`));
  } else {
    const daRaccontare = foto.filter((f) => !f.narrated);
    if (daRaccontare.length > 0) {
      intro.append(
        el('button', {
          class: 'btn-secondario',
          click: () => {
            const scelta = daRaccontare[Math.floor(Math.random() * daRaccontare.length)]!;
            location.hash = `#registra?mode=ascolto&foto=${scelta.id}`;
          },
        }, 'Racconta una fotografia'),
      );
    }
    const cards: Array<{ photo: FotoCard; node: HTMLElement }> = [];
    const sfoglia: SfogliaFoto = {
      // Solo le foto ancora visibili E ancora attaccate alla pagina: se la griglia è stata
      // rifatta sotto il dialogo, l'elenco si svuota e i pulsanti spariscono invece di mentire.
      lista: () => cards.filter((c) => !c.node.hidden && c.node.isConnected).map((c) => c.photo),
      nodo: (cercata) => cards.find((c) => c.photo.id === cercata.id)?.node ?? null,
    };
    cards.push(...foto.map((f) => ({ photo: f, node: el('button', {
            click: (ev: Event) => apriFotoPiena(f, ev.currentTarget as HTMLElement, sfoglia),
          }, el('img', { src: `/api/media/${f.id}?w=${LARGHEZZA_GRIGLIA}`, alt: '', loading: 'lazy' }),
            el('span', { class: 'photo-caption' }, f.caption || 'Una fotografia di famiglia',
              el('small', {}, f.narrated ? 'Con una storia' : 'Da raccontare')),
          ) })));
    const count = el('p', { class: 'data-oggi mb-3', role: 'status' }, `${foto.length} fotografie`);
    let filtro = 'all';
    const normalizza = (text: string): string => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('it');
    const cerca = el('input', { type: 'search', class: 'scrivi-testo', 'aria-label': 'Cerca fotografie', placeholder: 'Un nome, un luogo, un anno…' }) as HTMLInputElement;
    const aggiorna = (): void => {
      const parole = normalizza(cerca.value.trim()).split(/\s+/).filter(Boolean);
      let visibili = 0;
      for (const { photo, node } of cards) {
        const testo = normalizza([photo.caption, photo.story_title, photo.memory_title, photo.captured_edtf, photo.captured_edtf ? fmtDate(photo.captured_edtf) : ''].filter(Boolean).join(' '));
        node.hidden = (filtro === 'waiting' && !!photo.narrated) || (filtro === 'narrated' && !photo.narrated) || !parole.every((p) => testo.includes(p));
        if (!node.hidden) visibili++;
      }
      count.textContent = visibili ? `${visibili} ${visibili === 1 ? 'fotografia' : 'fotografie'}` : parole.length ? 'Nessuna fotografia trovata. Prova altre parole o cambia filtro.' : 'Non ci sono fotografie in questa sezione.';
    };
    cerca.addEventListener('input', aggiorna);
    const ricerca = el('div', { class: 'carta', id: 'foto-ricerca' },
      el('label', { for: 'foto-cerca' }, 'Cerca fotografie'),
      cerca,
      el('p', { class: 'didascalia' }, 'Cerca nelle didascalie, nei titoli delle storie e nelle date delle foto. Per esempio: giardino, un nome o 1969.'),
      el('button', { class: 'btn-quieto', click: () => { cerca.value = ''; aggiorna(); cerca.focus(); } }, 'Azzera ricerca'),
    );
    cerca.id = 'foto-cerca';
    ricerca.hidden = true;
    const btnCerca = el('button', { class: 'btn-secondario mb-3', 'aria-expanded': 'false', 'aria-controls': 'foto-ricerca', click: () => {
      ricerca.hidden = !ricerca.hidden;
      btnCerca.setAttribute('aria-expanded', String(!ricerca.hidden));
      if (!ricerca.hidden) cerca.focus();
      else { cerca.value = ''; aggiorna(); }
    } }, 'Cerca 🔍');
    const filters = el('div', { class: 'photo-filters', 'aria-label': 'Filtra le fotografie' });
    for (const [value, label] of [['all', 'Tutte'], ['waiting', 'Da raccontare'], ['narrated', 'Con una storia']] as const) {
      filters.append(el('button', { 'aria-pressed': String(value === 'all'), click: (ev: Event) => {
        for (const b of filters.querySelectorAll('button')) b.setAttribute('aria-pressed', String(b === ev.currentTarget));
        filtro = value;
        aggiorna();
      } }, label));
    }
    gallery.append(btnCerca, ricerca);
    gallery.append(filters, count, el('div', { class: 'griglia-foto' }, ...cards.map(({ node }) => node)));
  }
  pezzi.push(el('div', { class: 'photo-workspace' }, intro, gallery));
  screen(...pezzi);
}

/* ---------- mettiamo in ordine: le conferme di mamma ---------- */

async function renderOrdine(): Promise<void> {
  const { items } = await api.get<{ items: Array<{ kind: string; ref: string; testo: string; correggibile: boolean; valore?: string | null; nome?: string; relazione?: string | null }> }>('/api/verifiche');
  let indice = 0;
  let sistemate = 0;

  const contenuto = el('div', { class: 'pila' });

  const fine = (): void => {
    contenuto.replaceChildren(
      el('div', { class: 'conferma-grande' },
        el('div', { class: 'spunta' }, '🌳'),
        el('h2', {}, sistemate > 0 ? `Grazie! Abbiamo sistemato ${sistemate === 1 ? 'una cosa' : `${sistemate} cose`} ✓` : 'Va bene, un\'altra volta!'),
        el('p', {}, sistemate > 0 ? 'L\'albero e le date dei tuoi racconti ora sono più precisi.' : 'Le cose da controllare restano qui, quando vorrai.'),
      ),
      el('button', { class: 'btn-primario', click: () => { location.hash = ''; } }, 'Torna all\'inizio'),
    );
  };

  const mostra = (): void => {
    if (indice >= items.length) { fine(); return; }
    const item = items[indice]!;
    const esitoEl = el('p', { class: 'didascalia', role: 'status' }, '');
    const rispondi = async (esito: 'confermato' | 'smentito', extra?: { valore?: string; nome?: string; relazione?: string }): Promise<void> => {
      try {
        await api.post('/api/verifiche', { kind: item.kind, ref: item.ref, esito, ...extra });
        sistemate++;
      } catch {
        esitoEl.textContent = 'Non sono riuscita a salvare: riprova.';
        return;
      }
      indice++;
      mostra();
    };
    const azioni = el('div', { class: 'pila' },
      el('button', { class: 'btn-verde', click: () => void rispondi('confermato') }, '✓  Sì, è giusto'),
      el('button', { class: 'btn-secondario', click: () => void rispondi('smentito') }, '✗  No, non è così'),
      item.correggibile
        ? el('button', { class: 'btn-secondario', click: () => {
            if (item.kind === 'person') {
              // Persona: nome e «chi è per te», già scritti — lei ritocca.
              const nomeIn = el('input', { class: 'scrivi-testo', value: item.nome ?? '', 'aria-label': 'Nome' }) as HTMLInputElement;
              const relIn = el('input', { class: 'scrivi-testo', value: item.relazione ?? '', placeholder: 'chi è per te (es. cugina, vicino di casa)…', 'aria-label': 'Chi è per te' }) as HTMLInputElement;
              azioni.replaceChildren(
                el('p', { class: 'didascalia' }, 'Sistemali tu: il nome giusto, e chi è per te.'),
                nomeIn,
                relIn,
                el('button', { class: 'btn-verde', click: () => {
                  void rispondi('confermato', { nome: nomeIn.value.trim() || undefined, relazione: relIn.value.trim() || undefined });
                } }, 'Salva così ✓'),
                el('button', { class: 'btn-quieto', click: mostra }, 'Annulla'),
              );
              nomeIn.focus();
              return;
            }
            const inp = el('input', { class: 'scrivi-testo', value: item.valore ?? '', placeholder: 'Scrivi tu la cosa giusta…', 'aria-label': 'Correzione' }) as HTMLInputElement;
            azioni.replaceChildren(
              el('p', { class: 'didascalia' }, 'Correggila tu (una data, un nome, una parola):'),
              inp,
              el('button', { class: 'btn-verde', click: () => { if (inp.value.trim()) void rispondi('confermato', { valore: inp.value.trim() }); } }, 'Salva così ✓'),
              el('button', { class: 'btn-quieto', click: mostra }, 'Annulla'),
            );
            inp.focus();
            inp.select();
          } }, '✏️  Quasi: correggo io')
        : null,
      el('button', { class: 'btn-quieto', click: () => { indice++; mostra(); } }, 'Non ricordo, passiamo oltre'),
    );
    contenuto.replaceChildren(
      el('p', { class: 'data-oggi' }, `${indice + 1} di ${items.length}`),
      el('div', { class: 'carta' },
        el('p', { class: 'domanda-testo' }, item.testo),
        esitoEl,
        azioni,
      ),
    );
  };

  if (items.length === 0) { fine(); } else { mostra(); }
  screen(testata('Mettiamo in ordine'), contenuto);
}

/* ---------- le voci della famiglia ---------- */

async function renderVoci(): Promise<void> {
  const voci = await api.get<Array<{ id: number; narrator: string; title: string | null; told_at: string; has_audio: number; kind?: string }>>('/api/voci');
  const pezzi: Array<Node> = [testata('Le voci della famiglia')];
  if (voci.length === 0) {
    pezzi.push(el('p', { class: 'vuoto' }, `Quando qualcuno della famiglia lascerà un racconto per te, lo troverai qui.`));
  } else {
    pezzi.push(el('p', { class: 'data-oggi' }, 'I ricordi e i saluti che i tuoi cari hanno lasciato nel tuo quaderno.'));
    for (const v of voci) {
      pezzi.push(
        el('button', { class: 'storia-riga', click: () => { location.hash = `#storia/${v.id}`; } },
          el('span', { class: 'icona-riga', 'aria-hidden': 'true' }, v.kind === 'saluto' ? '💌' : v.has_audio ? '🎤' : '✏️'),
          el('div', {},
            el('div', {}, v.title ?? `Il ricordo di ${v.narrator}`),
            el('div', { class: 'quando' }, `${v.narrator} · ${fmtDate(v.told_at)}`),
          ),
        ),
      );
    }
  }
  screen(...pezzi);
}

/* ---------- albero di famiglia ---------- */

type GraphNode = {
  id: number; name: string; relation: string | null; photoMediaId: number | null;
  birth: string | null; death: string | null; status: 'confirmed' | 'proposed';
  gen: number | null; isMamma?: boolean;
};
type FamilyGraph = {
  mamma: string;
  nodes: GraphNode[];
  unions: Array<{ id: number; a: number; b: number | null; status: string }>;
  children: Array<{ unionId: number; childId: number; status: string }>;
};


/* ---------- scelta modalità ---------- */

async function renderScegli(): Promise<void> {
  const openedAt = navigation;
  // La telefonata è un esperimento: la carta compare solo se l'interruttore admin è acceso.
  const stato = await api.get<{ telefonata?: boolean }>('/api/state').catch(() => null);
  if (openedAt !== navigation) return;
  screen(
    testata('Come preferisci?'),
    el('div', { class: 'pila' },
      el('div', { class: 'carta' },
        el('p', { class: 'domanda-testo' }, 'Racconto io'),
        el('p', {}, 'Parli tu, con calma: io ascolto e basta, senza interrompere.'),
        el('button', { class: 'btn-primario', click: () => { location.hash = '#registra?mode=ascolto'; } }, 'Racconto io'),
      ),
      el('div', { class: 'carta' },
        el('p', { class: 'domanda-testo' }, 'Parliamo insieme'),
        el('p', {}, 'Racconti e io ogni tanto ti faccio una domanda, come in una chiacchierata.'),
        el('button', { class: 'btn-primario', click: () => { location.hash = '#registra?mode=dialogo'; } }, 'Parliamo insieme'),
      ),
      stato?.telefonata
        ? el('div', { class: 'carta carta-telefonata' },
            el('p', { class: 'domanda-testo' }, '📞 Telefona al quaderno'),
            el('p', {}, 'Parlate senza premere niente, come al telefono (è una prova).'),
            el('button', { class: 'btn-primario', click: () => { location.hash = '#registra?mode=telefonata'; } }, '📞  Telefona al quaderno'),
          )
        : null,
    ),
  );
}

/* ---------- le domande silenziose ---------- */

/** La riga delle domande silenziose, in «Racconto io» e in «Parliamo insieme». La domanda si
 *  chiede in una richiesta a parte e non si aspetta mai: la registrazione non ne dipende
 *  (regola 4). Arriva dopo una decina di secondi e si mostra per 25, solo se `puoMostrare` è
 *  ancora vero per quella registrazione. Mai voce: finirebbe nel racconto. */
function rigaSilenziosa(puoMostrare: (recordingId: string) => boolean): {
  el: HTMLElement; chiedi: (recordingId: string, questionId: number | null) => void; nascondi: () => void;
} {
  const riga = el('p', { class: 'domanda-silenziosa', role: 'status', hidden: '' });
  let timer: number | null = null, inVolo = false;
  function nascondi(): void {
    if (timer !== null) { clearTimeout(timer); timer = null; }
    riga.hidden = true;
  }
  function chiedi(recordingId: string, questionId: number | null): void {
    if (!riga.hidden || inVolo) return;
    inVolo = true;
    const ctrl = new AbortController();
    const scade = window.setTimeout(() => ctrl.abort(), 40_000);
    void fetch('/api/ascolto/domanda', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ recordingId, questionId: questionId ?? undefined }), signal: ctrl.signal })
      .then(async (r) => {
        if (r.status !== 200) return;
        const { domanda } = await r.json() as { domanda?: string };
        if (!domanda || !puoMostrare(recordingId)) return;
        riga.textContent = domanda;
        riga.hidden = false;
        timer = window.setTimeout(nascondi, 25_000);
      })
      .catch(() => undefined)
      .finally(() => { clearTimeout(scade); inVolo = false; });
  }
  return { el: riga, chiedi, nascondi };
}

/* ---------- registrazione: modalità ascolto ---------- */

async function renderAscolto(questionId: number | null, fotoId: number | null = null): Promise<void> {
  const openedAt = navigation;
  let questionText = 'Racconta quello che vuoi. Quando hai finito, tocca «Ho finito».';
  let fotoInfo: FotoCard | null = null;
  if (fotoId) {
    fotoInfo = await api.get<FotoCard>(`/api/photos/${fotoId}`).catch(() => null);
    questionText = 'Guarda questa fotografia con calma e racconta: chi c’è? Dove eravate? Che giornata era?';
  } else if (questionId) {
    const q = await api.get<QuestionInfo>(`/api/questions/${questionId}`).catch(() => null);
    if (q) questionText = q.text;
  }
  const fotoGrande = () =>
    fotoId
      ? el('div', {},
          el('img', { class: 'foto-racconto', 'data-ruolo': 'volto', src: `/api/media/${fotoId}?w=${LARGHEZZA_PAGINA_FOTO}`, alt: 'La fotografia da raccontare' }),
          fotoInfo?.caption ? el('p', { class: 'foto-racconto-didascalia' }, fotoInfo.caption) : null,
        )
      : null;

  if (openedAt !== navigation) return;
  const { sessionId } = await api.post<{ sessionId: number }>('/api/sessions', { mode: 'ascolto', questionId: questionId ?? undefined });
  if (openedAt !== navigation) {
    void api.post(`/api/sessions/${sessionId}/end`).catch(() => undefined);
    return;
  }
  let memoryId: number | null = null;
  let recorder: Recorder | null = null;

  // Se naviga via mentre registra, la storia si salva comunque.
  onLeaveScreen = () => {
    const rec = recorder;
    recorder = null;
    if (!rec || rec.stopped) return;
    void (async () => {
      try {
        const r = await rec.stop();
        if (r.durationS >= 2) {
          await finishRecording(r.recordingId, {
            sessionId,
            questionId: memoryId ? undefined : questionId ?? undefined,
            appendToMemoryId: memoryId ?? undefined,
            durationS: r.durationS,
            expectedChunks: r.chunkCount,
          });
        }
      } catch { /* i chunk restano al sicuro; lo sweeper del server li recupera */ }
    })();
  };

  const timerEl = el('p', { class: 'timer', 'aria-hidden': 'true' }, '0:00');
  const statoEl = el('p', { class: 'stato-ascolto', role: 'status' }, '');
  // Le domande silenziose (se Vincenzo le ha accese): in una pausa, una domanda scritta che
  // l'aiuta a continuare. Una richiesta a parte, mai attesa: la registrazione non ne dipende.
  const silenziose = (await api.get<{ domandeSilenziose?: boolean }>('/api/state').catch(() => null))?.domandeSilenziose === true;
  const sussurro = rigaSilenziosa((id) => recorder?.recordingId === id && !recorder.stopped && !recorder.paused);
  let secondiRegistrati = 0, ultimoSilenzio = 0, chiestaInQuestaPausa = false;
  const livelloBar = el('div');
  const livelloEl = el('div', { class: 'livello', 'aria-hidden': 'true' }, livelloBar);

  function nuovoRegistratore(): Recorder {
    return new Recorder({
      onTick: (s) => { timerEl.textContent = fmtTimer(s); secondiRegistrati = s; },
      onLevel: (v) => { livelloBar.style.width = `${Math.round(v * 100)}%`; },
      onSilence: (s) => {
        if (s > 75) statoEl.textContent = 'Vuoi continuare o abbiamo finito per oggi?';
        // Una pausa nuova ricomincia da zero: una sola domanda per pausa, dopo almeno 45 secondi.
        if (s < ultimoSilenzio) chiestaInQuestaPausa = false;
        ultimoSilenzio = s;
        if (silenziose && !chiestaInQuestaPausa && s >= 2.5 && secondiRegistrati >= 45 && recorder?.recordingId) {
          chiestaInQuestaPausa = true;
          sussurro.chiedi(recorder.recordingId, questionId);
        }
      },
      onError: (m) => { statoEl.textContent = m; },
    });
  }

  async function avvia(): Promise<void> {
    const next = nuovoRegistratore();
    recorder = next;
    statoEl.textContent = 'Un momento…';
    try {
      await next.start({ purpose: 'memory', sessionId, photoMediaId: fotoId ?? undefined });
    } catch {
      statoEl.textContent = `Non riesco a usare il microfono. Controlla i permessi insieme a ${caro}.`;
      return;
    }
    if (openedAt !== navigation || recorder !== next) {
      // Il permesso del microfono può arrivare quando la pagina è già cambiata.
      const saved = await next.stop();
      if (saved.chunkCount > 0) await finishRecording(saved.recordingId, { sessionId, durationS: saved.durationS, expectedChunks: saved.chunkCount });
      return;
    }
    statoEl.textContent = 'Ti sto ascoltando…';
    vistaRegistrazione();
  }

  function vistaIniziale(): void {
    screen(
      testata('Racconta'),
      el('div', { class: 'domanda-fissa' }, questionText),
      fotoGrande(),
      el('div', { class: 'zona-registra', 'data-ruolo': 'registra' },
        el('button', { class: 'pulsantone', click: avvia },
          el('span', { class: 'mic' }, '🎙️'), 'Tocca e racconta'),
        statoEl,
        el('button', { class: 'btn-quieto', click: () => vistaScrittura() }, '✍️  Preferisco scrivere'),
      ),
    );
  }

  // La tastiera come alternativa piena: il racconto scritto entra nell'archivio
  // esattamente come uno vocale (stessa pipeline, stessa estrazione).
  function vistaScrittura(): void {
    const ta = el('textarea', { class: 'scrivi-testo grande', rows: '10', placeholder: 'Scrivi qui il tuo racconto, con tutta la calma che vuoi…', 'aria-label': 'Scrivi il tuo racconto' }) as HTMLTextAreaElement;
    const salvaBtn = el('button', { class: 'btn-verde' }, 'Salva il racconto ✓') as HTMLButtonElement;
    const erroreEl = el('p', { role: 'status' }, '');
    salvaBtn.addEventListener('click', async () => {
      const t = ta.value.trim();
      if (t.length < 3) { erroreEl.textContent = 'Scrivi qualche parola, poi tocca Salva.'; return; }
      salvaBtn.disabled = true;
      salvaBtn.textContent = 'Sto salvando…';
      try {
        const res = await api.post<{ memoryId: number }>('/api/memories/text', { text: t, questionId: questionId ?? undefined });
        onLeaveScreen = null;
        renderConferma(res.memoryId);
      } catch {
        erroreEl.className = 'avviso';
        erroreEl.textContent = 'Non riesco a salvare adesso: il testo resta qui, riprova tra un momento.';
        salvaBtn.disabled = false;
        salvaBtn.textContent = 'Salva il racconto ✓';
      }
    });
    screen(
      testata('Scrivi il tuo racconto'),
      el('div', { class: 'domanda-fissa' }, questionText),
      el('div', { class: 'scrivi-area' }, ta, salvaBtn, erroreEl),
      el('button', { class: 'btn-quieto', click: () => vistaIniziale() }, '🎙️  Preferisco raccontare a voce'),
    );
    ta.focus();
  }

  function vistaRegistrazione(): void {
    const pausaBtn = el('button', { class: 'btn-secondario' }, 'Pausa');
    pausaBtn.addEventListener('click', () => {
      if (!recorder) return;
      if (recorder.paused) {
        recorder.resume();
        pausaBtn.textContent = 'Pausa';
        statoEl.textContent = 'Ti sto ascoltando…';
      } else {
        recorder.pause();
        sussurro.nascondi();
        pausaBtn.textContent = 'Continua';
        statoEl.textContent = 'In pausa. Prenditi il tempo che vuoi.';
      }
    });
    screen(
      testata('Sto ascoltando'),
      el('div', { class: 'domanda-fissa' }, questionText),
      sussurro.el,
      fotoGrande(),
      el('div', { class: 'zona-registra', 'data-ruolo': 'registra' },
        el('div', { class: 'pulsantone registrando' }, el('span', { class: 'mic' }, '🎙️'), 'Sto registrando'),
        timerEl, livelloEl, statoEl,
        el('div', { class: 'controlli-registra' },
          pausaBtn,
          el('button', { class: 'btn-verde', click: () => void fineParte() }, 'Ho finito'),
        ),
      ),
    );
  }

  async function fineParte(): Promise<void> {
    if (!recorder) return;
    sussurro.nascondi();
    statoEl.textContent = 'Sto salvando…';
    const rec = recorder;
    recorder = null;
    const mime = rec.mime || 'audio/webm';
    try {
      const { recordingId, durationS, chunkCount, allUploaded } = await rec.stop();
      await vistaRivedi(recordingId, durationS, chunkCount, allUploaded, mime);
    } catch {
      rec.cleanupHardware();
      screen(
        testata('La tua storia'),
        el('div', { class: 'avviso' }, 'C’è stato un intoppo nel salvataggio, ma la tua voce è al sicuro sul tablet. Riprova tra un momento.'),
        el('button', { class: 'btn-primario', click: () => { location.hash = '#home'; } }, 'Torna alla pagina iniziale'),
      );
    }
  }

  async function vistaRivedi(
    recordingId: string,
    durationS: number,
    chunkCount: number,
    allUploaded: boolean,
    mime: string,
  ): Promise<void> {
    const blob = await localBlob(recordingId, mime).catch(() => null);
    const audioEl = blob ? el('audio', { controls: '', src: URL.createObjectURL(blob) }) : null;

    async function salva(): Promise<number> {
      const res = await finishRecording<{ memoryId?: number; mediaId: number }>(recordingId, {
        sessionId,
        questionId: memoryId ? undefined : questionId ?? undefined,
        durationS,
        appendToMemoryId: memoryId ?? undefined,
        expectedChunks: chunkCount,
      });
      // Solo ora, con il server che ha verificato tutti i chunk, la copia locale può andare.
      void deleteRecording(recordingId);
      memoryId = res.memoryId ?? memoryId;
      return memoryId!;
    }

    const btnVaBene = el('button', { class: 'btn-verde' }, 'Va bene così ✓') as HTMLButtonElement;
    const btnAggiungi = el('button', { class: 'btn-secondario' }, 'Aggiungi qualcosa') as HTMLButtonElement;
    const btnScarta = el('button', { class: 'btn-quieto' }, 'Non tenerla') as HTMLButtonElement;
    const erroreEl = el('p', { role: 'status' }, '');
    const disableAll = (v: boolean) => { btnVaBene.disabled = v; btnAggiungi.disabled = v; btnScarta.disabled = v; };

    btnVaBene.addEventListener('click', async () => {
      disableAll(true);
      btnVaBene.textContent = 'Sto salvando…';
      try {
        const id = await salva();
        await api.post(`/api/sessions/${sessionId}/end`).catch(() => undefined);
        onLeaveScreen = null;
        renderConferma(id);
      } catch {
        erroreEl.textContent = 'Non riesco a salvare in questo momento: la registrazione è al sicuro, riprova tra poco.';
        erroreEl.className = 'avviso';
        btnVaBene.textContent = 'Va bene così ✓';
        disableAll(false);
      }
    });
    btnAggiungi.addEventListener('click', async () => {
      disableAll(true);
      try {
        await salva();
        await avvia();
      } catch {
        erroreEl.textContent = 'Non riesco a salvare in questo momento: riprova tra poco.';
        erroreEl.className = 'avviso';
        disableAll(false);
      }
    });
    btnScarta.addEventListener('click', async () => {
      disableAll(true);
      if (memoryId) await api.post(`/api/memories/${memoryId}/hide`).catch(() => undefined);
      void deleteRecording(recordingId);
      onLeaveScreen = null;
      location.hash = '#home';
    });

    screen(
      testata('La tua storia'),
      el('div', { class: 'carta riascolta' },
        el('p', { class: 'domanda-testo' }, `Ho registrato ${fmtTimer(Math.max(durationS, 1))} minuti della tua voce.`),
        allUploaded ? null : el('p', { class: 'avviso' }, 'Un pezzetto sta ancora arrivando al server: ci penso io, la tua voce è al sicuro.'),
        audioEl ? el('p', {}, 'Se vuoi, riascoltala:') : null,
        audioEl,
      ),
      el('div', { class: 'pila' }, btnVaBene, btnAggiungi, btnScarta),
      erroreEl,
    );
  }

  vistaIniziale();
}

/* ---------- registrazione: modalità dialogo ---------- */

async function renderDialogo(questionId: number | null, fotoIds: number[] = [], ricetta = false): Promise<void> {
  const openedAt = navigation;
  let questionText: string | null = null;
  let questionVoice: string | null = null;
  if (questionId) {
    const q = await api.get<QuestionInfo>(`/api/questions/${questionId}`).catch(() => null);
    if (q) {
      questionText = q.text;
      questionVoice = q.voiceUrl;
    }
  }
  if (openedAt !== navigation) return;
  const { sessionId, domandeSilenziose } = await api.post<{ sessionId: number; domandeSilenziose?: boolean }>('/api/sessions', {
    mode: 'dialogo',
    questionId: questionId ?? undefined,
    photoMediaIds: fotoIds.length > 0 ? fotoIds : undefined,
    kind: ricetta ? 'ricetta' : undefined,
  });
  if (openedAt !== navigation) {
    void api.post(`/api/sessions/${sessionId}/end`).catch(() => undefined);
    return;
  }

  const chat = el('div', { class: 'chat' });
  const appunti = taccuino();
  const statoEl = el('p', { class: 'stato-ascolto', role: 'status' }, '');
  const timerEl = el('p', { class: 'timer', hidden: '', 'aria-hidden': 'true' }, '0:00');
  const livelloBar = el('div');
  const livelloEl = el('div', { class: 'livello', hidden: '', 'aria-hidden': 'true' }, livelloBar);
  let recorder: Recorder | null = null;
  let occupato = false;
  // Le domande silenziose (se Vincenzo le ha accese e lei ha detto sì): la riga scritta di
  // «Racconto io», dentro un suo turno. Solo finché il turno è suo: chiuderlo la nasconde, e
  // una domanda arrivata dopo, mentre il quaderno le risponde, non si mostra. Sospesa in cima
  // allo schermo: comparendo non deve spostare i bottoni del turno (styles.css).
  const sussurro = rigaSilenziosa((id) => recorder?.recordingId === id && !recorder.stopped && !recorder.paused && !occupato);
  sussurro.el.classList.add('sospesa');

  // Se naviga via a metà chiacchierata: turno in corso al sicuro + sessione chiusa
  // (i turni già fatti diventano comunque un ricordo).
  onLeaveScreen = () => {
    const rec = recorder;
    recorder = null;
    void (async () => {
      try {
        if (rec && !rec.stopped) {
          const r = await rec.stop();
          if (r.durationS >= 2) {
            await finishRecording(r.recordingId, {
              sessionId,
              durationS: r.durationS,
              expectedChunks: r.chunkCount,
              orphanTurn: true,
            });
          }
        }
      } catch { /* lo sweeper del server recupera */ }
      try {
        await fetch(`/api/sessions/${sessionId}/end`, { method: 'POST', keepalive: true });
      } catch { /* idem */ }
    })();
  };

  const micBtn = el('button', { class: 'pulsantone' }, el('span', { class: 'mic' }, '🎙️'), 'Tocca e parla') as HTMLButtonElement;
  const fineTurnoBtn = el('button', { class: 'btn-verde', hidden: '' }, 'Ho finito di parlare') as HTMLButtonElement;
  const pausaTurnoBtn = el('button', { class: 'btn-secondario', hidden: '' }, '⏸  Pausa') as HTMLButtonElement;
  pausaTurnoBtn.addEventListener('click', () => {
    if (!recorder) return;
    if (recorder.paused) {
      recorder.resume();
      pausaTurnoBtn.textContent = '⏸  Pausa';
      statoEl.textContent = 'Ti ascolto di nuovo.';
    } else {
      recorder.pause();
      sussurro.nascondi();
      pausaTurnoBtn.textContent = '▶  Continua';
      statoEl.textContent = 'In pausa: prenditi il tempo che vuoi.';
    }
  });
  const bastaBtn = el('button', { class: 'btn-quieto' }, 'Basta per oggi') as HTMLButtonElement;

  function bolla(classe: string, testo: string): HTMLElement {
    const b = el('div', { class: `bolla ${classe}` }, testo);
    chat.append(b);
    b.scrollIntoView({ block: 'end' });
    return b;
  }

  const composer = el('div', { class: 'scrivi-area', hidden: '' });
  const scriviTa = el('textarea', { class: 'scrivi-testo', rows: '4', placeholder: 'Scrivi qui quello che vuoi dire…', 'aria-label': 'Scrivi la tua risposta' }) as HTMLTextAreaElement;
  const inviaBtn = el('button', { class: 'btn-verde' }, 'Invia ✉️') as HTMLButtonElement;
  const scriviBtn = el('button', { class: 'btn-quieto' }, '✍️  Preferisco scrivere') as HTMLButtonElement;
  scriviBtn.addEventListener('click', () => {
    const aperto = !composer.hidden;
    composer.hidden = aperto;
    if (!aperto) scriviTa.focus();
  });
  inviaBtn.addEventListener('click', async () => {
    const t = scriviTa.value.trim();
    if (!t || occupato) return;
    scriviTa.value = '';
    composer.hidden = true;
    await inviaTesto(t);
  });
  composer.append(scriviTa, inviaBtn);

  const fotoStrip = el('div', { class: 'strip-foto' },
    ...fotoIds.map((fid) => el('img', { src: `/api/media/${fid}?w=${LARGHEZZA_GRIGLIA}`, alt: 'Fotografia che stai raccontando' })));
  fotoStrip.hidden = fotoIds.length === 0;
  const fotoDialogoInput = el('input', { type: 'file', accept: 'image/*', multiple: '', hidden: '' }) as HTMLInputElement;
  const fotoDialogoBtn = el('button', { class: 'btn-quieto' }, '📷  Aggiungi una foto') as HTMLButtonElement;
  fotoDialogoBtn.addEventListener('click', () => fotoDialogoInput.click());
  fotoDialogoInput.addEventListener('change', async () => {
    const files = Array.from(fotoDialogoInput.files ?? []);
    if (files.length === 0) return;
    fotoDialogoBtn.disabled = true;
    fotoDialogoBtn.textContent = 'Sto caricando…';
    try {
      const ids = await caricaFoto(files);
      await api.post(`/api/sessions/${sessionId}/photos`, { photoMediaIds: ids });
      for (const fid of ids) fotoStrip.append(el('img', { src: `/api/media/${fid}?w=${LARGHEZZA_GRIGLIA}`, alt: 'Fotografia aggiunta' }));
      fotoStrip.hidden = false;
      bolla('bolla-nota', ids.length === 1 ? 'Foto aggiunta: la guardo al tuo prossimo messaggio.' : 'Foto aggiunte: le guardo al tuo prossimo messaggio.');
    } catch {
      bolla('bolla-nota', 'Non sono riuscita a caricare le foto: riprova.');
    } finally {
      fotoDialogoBtn.disabled = false;
      fotoDialogoBtn.textContent = '📷  Aggiungi una foto';
      fotoDialogoInput.value = '';
    }
  });

  micBtn.addEventListener('click', () => void iniziaTurno());
  fineTurnoBtn.addEventListener('click', () => void chiudiTurno());
  bastaBtn.addEventListener('click', () => void fineSessione());

  async function iniziaTurno(): Promise<void> {
    if (occupato || recorder) return;
    stopSpeaking();
    let secondiTurno = 0, parlatoAllaPausa: number | null = null;
    recorder = new Recorder({
      onTick: (s) => { timerEl.textContent = fmtTimer(s); secondiTurno = s; },
      onLevel: (v) => { livelloBar.style.width = `${Math.round(v * 100)}%`; },
      onSilence: (s) => {
        // Le pause fanno parte del racconto: prima si avvisa, poi (con calma) si chiude.
        if (s >= 6) { void chiudiTurno(); }
        else if (s >= 3.5) { statoEl.textContent = 'Se hai finito, tocca «Ho finito di parlare» — altrimenti continua pure, ti ascolto.'; }
        // Una pausa lunga (2,5 s, prima ancora dell'avviso) che NON ha chiuso il turno: si annota
        // quanto aveva parlato fin lì. La domanda silenziosa si chiede quando il parlato è
        // cresciuto, cioè quando ha ripreso davvero a raccontare (dopo 45 s di turno): arriva
        // mentre racconta, pronta per la pausa dopo. Il silenzio che riparte da capo non basta:
        // «Continua» dopo la «Pausa» lo azzera anche se lei resta zitta, e il turno si
        // chiuderebbe proprio mentre Whisper è occupato con la domanda. Chiederla dentro la pausa
        // nemmeno: con 6 s per chiudere il turno arriverebbe sempre a turno chiuso.
        const parlato = recorder?.vadStats().speechMs ?? 0;
        if (s >= 2.5) parlatoAllaPausa = parlato;
        else if (parlatoAllaPausa !== null && parlato > parlatoAllaPausa) {
          parlatoAllaPausa = null;
          if (domandeSilenziose && secondiTurno >= 45 && recorder?.recordingId) sussurro.chiedi(recorder.recordingId, questionId);
        }
      },
      onError: (m) => { statoEl.textContent = m; },
    });
    try {
      await recorder.start({ purpose: 'turn', sessionId });
    } catch {
      recorder = null;
      statoEl.textContent = `Non riesco a usare il microfono. Controlla i permessi insieme a ${caro}.`;
      return;
    }
    micBtn.classList.add('registrando');
    micBtn.replaceChildren(el('span', { class: 'mic' }, '🎙️'), 'Ti ascolto…');
    timerEl.hidden = false;
    livelloEl.hidden = false;
    fineTurnoBtn.hidden = false;
    pausaTurnoBtn.hidden = false;
    pausaTurnoBtn.textContent = '⏸  Pausa';
    statoEl.textContent = 'Parla pure. Quando smetti, ci penso io.';
  }

  async function chiudiTurno(): Promise<void> {
    if (!recorder || occupato) return;
    occupato = true;
    sussurro.nascondi(); // il quaderno sta per risponderle
    const rec = recorder;
    recorder = null;
    micBtn.classList.remove('registrando');
    micBtn.replaceChildren(el('span', { class: 'mic' }, '⏳'), 'Un momento…');
    micBtn.disabled = true;
    fineTurnoBtn.hidden = true;
    pausaTurnoBtn.hidden = true;
    timerEl.hidden = true;
    livelloEl.hidden = true;
    statoEl.textContent = 'Ci sto pensando…';

    try {
      const { recordingId, durationS, chunkCount } = await rec.stop();
      if (durationS < 1) {
        statoEl.textContent = 'Non ho sentito nulla: proviamo di nuovo?';
        void deleteRecording(recordingId);
        return;
      }
      const { mediaId } = await finishRecording<{ mediaId: number }>(recordingId, {
        sessionId,
        durationS,
        expectedChunks: chunkCount,
      });
      void deleteRecording(recordingId);
      await scambiaTurno({ sessionId, mediaId });
    } catch {
      bolla('bolla-nota', 'La rete ha fatto i capricci, ma la tua voce è salvata. Riprova quando vuoi.');
    } finally {
      occupato = false;
      micBtn.disabled = false;
      micBtn.replaceChildren(el('span', { class: 'mic' }, '🎙️'), 'Tocca e rispondi');
      statoEl.textContent ||= '';
    }
  }

  // Parte condivisa del turno (voce o testo): invio → risposta in streaming → voce.
  async function scambiaTurno(body: Record<string, unknown>): Promise<void> {
    let bollaApp: HTMLElement | null = null;
    let rispostaCompleta = '';
    let audioUrl: string | null = null;

    await postSse('/api/conversation/turn', body, {
      transcript: (d) => { bolla('bolla-mamma', String(d.text ?? '')); },
      appunti: (d) => { appunti.aggiorna(d); },
      delta: (d) => {
        if (!bollaApp) bollaApp = bolla('bolla-app', '');
        rispostaCompleta += String(d.text ?? '');
        bollaApp.textContent = rispostaCompleta;
        bollaApp.scrollIntoView({ block: 'end' });
      },
      audio: (d) => { audioUrl = String(d.url ?? '') || null; },
      error: (d) => { bolla('bolla-nota', String(d.message ?? 'Qualcosa non ha funzionato.')); },
      done: (d) => {
        const testo = String(d.text ?? rispostaCompleta);
        if (!bollaApp && testo) bollaApp = bolla('bolla-app', testo);
        rispostaCompleta = testo;
      },
    });

    statoEl.textContent = '';
    if (rispostaCompleta) {
      if (audioUrl) await playUrl(audioUrl).catch(() => undefined);
      else await speak(rispostaCompleta);
    }
  }

  // "Scrivi": la tastiera è un'alternativa di pari dignità alla voce.
  async function inviaTesto(testo: string): Promise<void> {
    if (occupato) return;
    occupato = true;
    micBtn.disabled = true;
    statoEl.textContent = 'Ci sto pensando…';
    try {
      await scambiaTurno({ sessionId, text: testo });
    } catch {
      bolla('bolla-nota', 'La rete ha fatto i capricci: riprova tra un momento.');
    } finally {
      occupato = false;
      micBtn.disabled = false;
      statoEl.textContent ||= '';
    }
  }

  async function fineSessione(): Promise<void> {
    stopSpeaking();
    bastaBtn.disabled = true;
    bastaBtn.textContent = 'Un momento…';
    // Se un turno è ancora in elaborazione, le sue parole devono entrare nel ricordo.
    if (recorder) await chiudiTurno().catch(() => undefined);
    await waitUntil(() => !occupato, 90_000);
    onLeaveScreen = null;
    const res = await api.post<{ ok: boolean; memoryId?: number }>(`/api/sessions/${sessionId}/end`).catch(() => ({ ok: false as const }));
    if (res && 'memoryId' in res && res.memoryId) renderConferma(res.memoryId);
    else location.hash = '#home';
  }

  screen(
    testata(ricetta ? 'La tua ricetta' : 'Parliamo insieme'),
    sussurro.el,
    questionText ? el('div', { class: 'domanda-fissa' }, questionText) : null,
    fotoStrip,
    chat,
    appunti.element,
    el('div', { class: 'zona-registra', 'data-ruolo': 'registra' }, micBtn, timerEl, livelloEl, statoEl, fineTurnoBtn, pausaTurnoBtn, scriviBtn, fotoDialogoBtn, fotoDialogoInput, composer, bastaBtn),
  );

  if (fotoIds.length > 0 || ricetta) {
    // Con foto o ricetta è l'app ad aprire la conversazione.
    statoEl.textContent = 'Sto guardando le tue fotografie…';
    let bollaApertura: HTMLElement | null = null;
    let testoApertura = '';
    let audioApertura: string | null = null;
    try {
      await postSse('/api/conversation/opener', { sessionId }, {
        delta: (d) => {
          if (!bollaApertura) bollaApertura = bolla('bolla-app', '');
          testoApertura += String(d.text ?? '');
          bollaApertura.textContent = testoApertura;
        },
        audio: (d) => { audioApertura = String(d.url ?? '') || null; },
        done: (d) => {
          const t = String(d.text ?? testoApertura);
          if (!bollaApertura && t) bollaApertura = bolla('bolla-app', t);
          testoApertura = t;
        },
        error: () => { /* si passa al fallback sotto */ },
      });
    } catch { /* fallback sotto */ }
    statoEl.textContent = '';
    if (!testoApertura) {
      testoApertura = ricetta
        ? 'Il ricettario è pronto! Quale piatto mi detti oggi?'
        : 'Che belle queste fotografie! Raccontami: chi c’è, e dove eravate?';
      bolla('bolla-app', testoApertura);
    }
    if (audioApertura) await playUrl(audioApertura).catch(() => speak(testoApertura));
    else await speak(testoApertura);
    return;
  }

  const apertura = questionText ?? 'Eccomi! Raccontami quello che vuoi: ti ascolto.';
  bolla('bolla-app', apertura);
  if (questionVoice) await playUrl(questionVoice).catch(() => speak(apertura));
  else await speak(apertura);
}

/* ---------- telefonata col quaderno (hands-free, esperimento) ---------- */

function renderTelefonata(): void {
  const { root, abandon } = buildCallScreen({
    caro,
    say: (t) => speak(t, { force: true }),
    onEnd: (memoryId) => {
      // Se mamma ha già cambiato schermata, la chiusura tardiva non deve toccare nulla.
      if (onLeaveScreen !== abandon) return;
      onLeaveScreen = null;
      if (memoryId) renderConferma(memoryId);
      else location.hash = '#home';
    },
  });
  onLeaveScreen = abandon;
  screen(testata('Telefonata col quaderno'), root);
}

/* ---------- conferma dopo il salvataggio ---------- */

function renderConferma(memoryId: number): void {
  const fotoInput = el('input', { type: 'file', accept: 'image/*', hidden: '' }) as HTMLInputElement;
  const stato = el('p', { role: 'status' }, '');
  fotoInput.addEventListener('change', async () => {
    const file = fotoInput.files?.[0];
    if (!file) return;
    stato.textContent = 'Sto caricando la foto…';
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`/api/memories/${memoryId}/media`, { method: 'POST', body: form });
    stato.textContent = res.ok ? 'Foto aggiunta al ricordo ✓' : 'Non sono riuscita a caricare la foto. Riprova.';
  });

  screen(
    el('div', { class: 'conferma-grande' },
      el('div', { class: 'spunta' }, '✅'),
      el('h2', {}, 'Bellissimo. La tua storia è nel tuo libro.'),
      el('p', {}, 'La sto trascrivendo con calma: tra poco la troverai nei tuoi ricordi.'),
      stato,
    ),
    el('div', { class: 'pila' },
      el('button', { class: 'btn-secondario', click: () => fotoInput.click() }, '📷  Vuoi aggiungere una foto?'),
      el('button', { class: 'btn-primario', click: () => { location.hash = '#home'; } }, 'Torna alla pagina iniziale'),
    ),
    fotoInput,
  );
}

/* ---------- temi, storie, famiglia, libro ---------- */

async function renderTema(slug: string): Promise<void> {
  const data = await api.get<{ theme: { title: string }; narrative: string | null; pageStatus: PageStatus; memories: MemoryCard[] }>(`/api/themes/${slug}`);
  // Ordinamento: la scelta vale per tutte le pagine dei temi.
  const ORDINE_KEY = 'memorie_ordine_storie';
  let ordine = 'nuove';
  try { ordine = localStorage.getItem(ORDINE_KEY) ?? 'nuove'; } catch { /* privata */ }
  const ordinate = [...data.memories];
  if (ordine === 'vecchie') ordinate.sort((a, b) => a.toldAt.localeCompare(b.toldAt));
  else if (ordine === 'anno') ordinate.sort((a, b) => chiaveData(a.eventDate ?? '9999').localeCompare(chiaveData(b.eventDate ?? '9999')));
  else ordinate.sort((a, b) => b.toldAt.localeCompare(a.toldAt));
  const chip = (v: string, label: string) =>
    el('button', { class: 'btn-quieto' + (ordine === v ? ' ordine-attivo' : ''), click: () => {
      try { localStorage.setItem(ORDINE_KEY, v); } catch { /* privata */ }
      render();
    } }, label);
  screen(
    testata(data.theme.title),
    pageEditor('theme', slug, data.narrative, data.pageStatus, () => renderTema(slug)),
    data.narrative
      ? el('div', { class: 'pagina-narrativa' },
          ...data.narrative.split(/\n+/).filter(Boolean).map((p) => el('p', {}, p)))
      : null,
    data.memories.length > 1
      ? el('div', { class: 'ordine-chips' },
          el('span', { class: 'quando' }, 'In ordine: '),
          chip('nuove', 'più recenti'), chip('vecchie', 'più vecchie'), chip('anno', 'per anno della storia'))
      : null,
    data.memories.length === 0
      ? el('p', { class: 'vuoto' }, 'Ancora nessun racconto qui.')
      : el('div', {},
          ...ordinate.map((m) =>
            el('button', { class: 'storia-riga', click: () => { location.hash = `#storia/${m.id}`; } },
              el('div', {},
                el('div', {}, m.title),
                el('div', { class: 'quando' }, ordine === 'anno' && m.eventDate ? fmtDate(m.eventDate) : `raccontato il ${fmtDate(m.toldAt)}`,
                  m.hasVideo ? segnoVideo() : null),
              ),
              m.hasAudio ? el('span', { class: 'play' }, '▶') : null,
            ),
          ),
        ),
  );
}

/** «Questa storia è nata da una domanda di Vincenzo»: il suo testo e, se c'è, la sua voce. */
function cartaOrigine(d: { id: number; text: string; voiceUrl: string | null }): HTMLElement {
  const voce = d.voiceUrl;
  return el('div', { class: 'carta carta-vincenzo' },
    el('p', { class: 'etichetta' }, `❓ Questa storia è nata da una domanda di ${caro}`),
    el('p', { class: 'citazione' }, `“${d.text}”`),
    voce ? el('button', { class: 'btn-secondario', 'data-ruolo': 'voce', click: () => void playUrl(voce) }, '▶  Ascolta la sua voce') : null,
  );
}

async function renderStoria(id: string, params?: URLSearchParams): Promise<void> {
  const caricata = await api.get<{
    id: number; title: string; text: string | null; inLavorazione: boolean; trascrizioneInCorso: boolean; narrator?: string;
    kind?: string; ricetta?: { nome: string; di_chi?: string | null; ingredienti: string[]; procedimento: string[]; segreto?: string | null; occasione?: string | null } | null;
    toldAt: string; sourcePhotoId: number | null;
    // La domanda di Vincenzo da cui è nato il racconto (assente sui server più vecchi).
    nataDaDomanda?: { id: number; text: string; voiceUrl: string | null } | null;
    media: Array<RigaMedia & { in_libreria?: number; guest_id?: number | null }>;
    // «🎞 Aggiungi un video»: c'è solo con i video accesi (assente sui server più vecchi).
    aggiungiVideo?: Regole | null;
    // La registrazione che lei ha nascosto (assente sui server più vecchi).
    registrazioneNascosta?: boolean;
    // Quanti pezzi della voce ha nascosto, anche con altri ancora visibili (assente sui server più vecchi).
    registrazioniNascoste?: number;
    // «👪 Falla leggere ai parenti»: null se la storia non può andarci o se nessun parente ha un link.
    vetrina?: { inVetrina: boolean; parenti: string[]; quante: number; max: number } | null;
    people: Array<{ id: number; display_name: string }>;
    reactions: Array<Reazione & { created_at: string }>;
  }>(`/api/memories/${id}`).catch((err: unknown) => {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  });
  // Dal link di una lettera può arrivare a una storia messa via nel frattempo: niente schermata d'errore.
  if (!caricata) {
    screen(
      testata('Questa storia non c’è più'),
      el('div', { class: 'avviso' }, 'Questa storia non c’è più: forse è stata messa via. Le altre ti aspettano nella Casa.'),
      el('a', { class: 'btn-secondario', href: '#home' }, 'Torna alla Casa'),
    );
    return;
  }
  const m = caricata; // le funzioni interne (hoisted) non vedono il controllo qui sopra
  // Il racconto di un parente: `narrator` è il suo nome, lo stesso di «Raccontato da …» qui sotto.
  // La voce che si sente è la sua, e la pagina lo dice: «La voce di Carla», mai «La tua voce».
  const parente = m.narrator && m.narrator !== 'mamma' ? m.narrator : null;

  const audio = m.media.filter((x) => x.kind === 'audio');
  const foto = m.media.filter((x) => x.kind === 'photo');
  const areaCorrezioni = el('div', { class: 'pila mt-2' });
  // I video della storia, dopo le fotografie: le carte, il caricamento in corso (che continua
  // anche cambiando pagina) e la scelta del file per «🎞 Aggiungi un video».
  const video = sezioneVideo(m.id, videoDellaStoria(m.media), { caro: () => caro, titolo: m.title, regole: m.aggiungiVideo ?? null });

  // La sua voce (richiesta #23, «un tasto per eliminare la registrazione»): «Nascondi» la toglie
  // dalla pagina, mai dal quaderno — il file e il racconto restano — e «Mostrala di nuovo» la
  // rimette, sempre. Il pulsante grande vale per TUTTI i pezzi: per lei è LA registrazione di questa
  // storia, anche quando il dialogo l'ha spezzata in più parti. Ma se i pezzi sono più d'uno uno può
  // essere un doppione (richiesta #28: la terza ripeteva la prima), e allora ogni pezzo ha il suo
  // «Nascondi questa»; quelli nascosti restano contati e si rimettono con «Mostra». La carta si
  // ridisegna sul posto con lo stato che risponde il server: niente pagina rifatta, niente salto in cima.
  function cartaVoce(pezzi: Array<{ id: number }>, nascosta: boolean, nascoste: number = nascosta ? 1 : 0, aperto = false): HTMLElement | null {
    const esito = el('p', { class: 'avviso', role: 'status', hidden: '' });
    const ridisegna = (r: { registrazioni: Array<{ id: number }>; nascosta: boolean; nascoste?: number }, fuoco: string): void => {
      const nuova = cartaVoce(r.registrazioni, r.nascosta, r.nascoste ?? (r.nascosta ? 1 : 0), voce?.querySelector<HTMLDetailsElement>('details')?.open === true);
      if (!nuova) return;
      voce?.replaceWith(nuova);
      voce = nuova;
      // Il fuoco non si perde col pulsante che sparisce: sul «Mostra», o sulla voce ritrovata.
      (nuova.querySelector<HTMLElement>(fuoco) ?? nuova.querySelector<HTMLElement>('button'))?.focus();
    };
    const chiama = async (percorso: string, btn: HTMLButtonElement, errore: string, fuoco: string): Promise<void> => {
      btn.disabled = true;
      try {
        ridisegna(await api.post<{ registrazioni: Array<{ id: number }>; nascosta: boolean; nascoste?: number }>(`/api/memories/${m.id}/registrazione/${percorso}`), fuoco);
      } catch {
        btn.disabled = false;
        esito.hidden = false;
        esito.textContent = errore;
      }
    };
    const cambia = (cosa: 'nascondi' | 'mostra', btn: HTMLButtonElement): Promise<void> =>
      chiama(cosa, btn, cosa === 'nascondi' ? 'Non sono riuscita a nasconderla: riprova.' : 'Non sono riuscita a rimetterla: riprova.', cosa === 'nascondi' ? 'button' : 'audio');
    // «Nascondi questa»: un pezzo solo. Dopo, il fuoco va al «Mostra», che la rimette se è stato un errore.
    const nascondiQuesta = (id: number, n: number): HTMLButtonElement => {
      const b = el('button', {
        class: 'btn-quieto mt-2', 'data-ruolo': 'nascondi-pezzo', 'aria-label': `Nascondi solo la registrazione ${n}`,
        click: () => void chiama(`${id}/nascondi`, b, 'Non sono riuscita a nasconderla: riprova.', '.registrazioni-mostra'),
      }, '🙈  Nascondi questa') as HTMLButtonElement;
      return b;
    };
    const mostraNascoste = (): HTMLElement | null => {
      if (nascoste === 0 || pezzi.length === 0) return null;
      const b = el('button', { class: 'btn-quieto registrazioni-mostra mt-2', click: () => void cambia('mostra', b) },
        `👂  ${nascoste === 1 ? '1 nascosta' : `${nascoste} nascoste`} · Mostra`) as HTMLButtonElement;
      return b;
    };
    if (pezzi.length > 0) {
      const separati = pezzi.length > 1;
      const nascondi = el('button', { class: 'btn-secondario mt-2', click: () => void cambia('nascondi', nascondi) }, '🙈  Nascondi la registrazione') as HTMLButtonElement;
      const dettagli = pezzi.length > 1 ? el('details', { class: 'altre-registrazioni', ...(aperto ? { open: '' } : {}) },
        el('summary', {}, `Altre registrazioni (${pezzi.length - 1})`),
        ...pezzi.slice(1).map((a, i) => el('div', {},
          el('p', { class: 'didascalia mt-3' }, `Registrazione ${i + 2}`),
          el('audio', { controls: '', 'data-ruolo': 'voce', preload: 'none', src: `/api/media/${a.id}`, 'aria-label': `Registrazione ${i + 2}` }),
          nascondiQuesta(a.id, i + 2),
        )),
      ) : null;
      return el('div', { class: 'carta riascolta' },
        el('p', { class: 'etichetta' }, parente ? `La voce di ${parente}` : 'La tua voce'),
        ...pezzi.slice(0, 1).map((a) => el('audio', { controls: '', 'data-ruolo': 'voce', preload: 'none', src: `/api/media/${a.id}`, 'aria-label': 'Prima registrazione della storia' })),
        separati ? nascondiQuesta(pezzi[0]!.id, 1) : null,
        dettagli,
        mostraNascoste(),
        nascondi,
        esito,
      );
    }
    if (!nascosta) return null;
    const mostra = el('button', { class: 'btn-quieto', click: () => void cambia('mostra', mostra) }, '👂  Mostrala di nuovo') as HTMLButtonElement;
    return el('div', { class: 'voce-nascosta' },
      el('p', { class: 'didascalia', role: 'status' }, 'La registrazione è nascosta. Il racconto scritto resta.'),
      mostra,
      esito,
    );
  }
  let voce = cartaVoce(audio, m.registrazioneNascosta === true, m.registrazioniNascoste ?? (m.registrazioneNascosta ? 1 : 0));

  // Con l'album, questa storia è anche una pagina del suo capitolo: la bottega resta qui
  // (correggi, il filo, la voce, «non tenerla»), ma da qui si può andare a sfogliarla.
  // Il posto lo chiede il server con una risposta piccola: nessun album scaricato per contare.
  const vaiAllAlbum = el('div', {});
  {
    void api.get<{ tipo: string; chiave: string; titolo: string; pagina: number; totale: number }>(`/api/album/dove/${m.id}`)
      .then((dove) => {
        if (!dove) return;
        vaiAllAlbum.append(el('button', {
          class: 'btn-secondario mb-3 album-vai',
          click: () => { location.hash = `#album/${dove.tipo}/${dove.chiave}/${dove.pagina}`; },
        }, `📖  Sfogliala in «${dove.titolo}» (pagina ${dove.pagina} di ${dove.totale})`));
      })
      .catch(() => undefined); // una storia senza capitolo non ha una pagina: si tace
  }

  screen(
    testata(m.title),
    el('p', { class: 'data-oggi' },
      parente
        ? `🎤 Raccontato da ${parente} il ${fmtDate(m.toldAt)}`
        : `Raccontato il ${fmtDate(m.toldAt)}`),
    vaiAllAlbum,
    el('div', { class: 'story-workspace' },
      el('aside', { class: 'story-companion', 'aria-label': 'Ascolto e messaggi' },
        m.text
          ? el('button', { class: 'btn-secondario mb-3', click: () => void speak(`${m.title}. ${m.text}`, { force: true }) },
              '🔊  Leggimi questa storia')
          : null,
        // Da dove è partita: la domanda di Vincenzo resta attaccata al racconto, con la sua voce.
        m.nataDaDomanda ? cartaOrigine(m.nataDaDomanda) : null,
        // Il filo con Vincenzo sta in alto, prima di foto e registrazioni: è la novità.
        m.reactions.length > 0 ? cartaFilo(m.id, m.reactions, () => void renderStoria(String(m.id))) : null,
        // Il racconto l'ha portato un parente: qui mamma gli risponde (lo legge lui, sulla sua pagina).
        parente ? cartaRispostaOspite(m.id) : null,
        voce,
      ),
      el('article', { class: 'story-reading', 'aria-label': m.title },
        ...(() => {
          // Le fotografie di questa storia si sfogliano fra loro, come quelle della galleria.
          const schede: Array<{ photo: FotoCard; node: HTMLElement }> = [];
          const sfoglia: SfogliaFoto = {
            lista: () => schede.filter((c) => c.node.isConnected).map((c) => c.photo),
            nodo: (cercata) => schede.find((c) => c.photo.id === cercata.id)?.node ?? null,
            daStoria: true,
          };
          // Il posto si conta fra le foto di QUESTA storia: la foto raccontata che sta in un'altra
          // (`altraStoria`, in cima) non ne fa parte, e «Toglila da questa storia» la staccherebbe
          // dalla sua vera storia — quindi per lei né posto né memory_id di questa pagina.
          const proprie = foto.filter((x) => !x.altraStoria).length;
          let posto = 0;
          if (foto.length === 0) return [];
          // Piccole e in fila, tre/quattro/cinque per riga (#33): toccandone una si vede intera.
          return [el('div', { class: 'storia-foto-griglia', 'data-ruolo': 'foto-della-storia' }, ...foto.map((f) => {
            const scheda: FotoCard = f.altraStoria
              ? { id: f.id, caption: f.caption, memory_id: null, memory_title: null, story_id: m.id, story_title: m.title, in_libreria: f.in_libreria, guest_id: f.guest_id }
              : { id: f.id, caption: f.caption, memory_id: m.id, memory_title: m.title, story_id: m.id, story_title: m.title, in_libreria: f.in_libreria, guest_id: f.guest_id, posto: ++posto, totale: proprie };
            const bottone = el('button', {
              class: 'foto-storia-btn',
              'aria-label': 'Apri le opzioni della fotografia',
              click: (ev: Event) => apriFotoPiena(scheda, ev.currentTarget as HTMLElement, sfoglia),
            }, el('img', { class: 'foto-storia', src: `/api/media/${f.id}?w=${LARGHEZZA_GRIGLIA}`, alt: f.caption ?? 'Fotografia del ricordo', loading: 'lazy', decoding: 'async' }));
            schede.push({ photo: scheda, node: bottone });
            return el('figure', { class: 'figura-storia' }, bottone, f.caption ? el('figcaption', {}, f.caption) : null);
          }))];
        })(),
        video.element,
        // Il disegno della storia (se i disegni sono accesi): dopo le sue foto, mai al posto loro.
        !parente && m.text && !m.trascrizioneInCorso ? cartaDisegno(m.id) : null,
        m.ricetta
          ? el('div', { class: 'carta ricetta-carta' },
              el('p', { class: 'etichetta' }, m.ricetta.di_chi ? `Ricetta ${m.ricetta.di_chi}` : 'Ricetta'),
              el('p', { class: 'domanda-testo' }, m.ricetta.nome),
              el('h3', { class: 'ricetta-sezione' }, 'Ingredienti'),
              el('ul', { class: 'ricetta-lista' }, ...m.ricetta.ingredienti.map((i) => el('li', {}, i))),
              el('h3', { class: 'ricetta-sezione' }, 'Procedimento'),
              el('ol', { class: 'ricetta-lista' }, ...m.ricetta.procedimento.map((pss) => el('li', {}, pss))),
              m.ricetta.segreto ? el('p', { class: 'ricetta-segreto' }, `✨ Il segreto: ${m.ricetta.segreto}`) : null,
              m.ricetta.occasione ? el('p', { class: 'quando' }, m.ricetta.occasione) : null,
            )
          : null,
        m.people.length > 0
          ? el('div', { class: 'storia-persone' },
              el('span', { class: 'con-chi' }, 'Con: '),
              ...m.people.map((p) =>
                el('button', { class: 'persona-chip', click: () => { location.hash = `#persona/${p.id}`; } }, p.display_name),
              ),
            )
          : null,
        el('div', { class: 'storia-testo' },
          m.trascrizioneInCorso
            ? el('p', { class: 'vuoto' }, `Sto ancora trascrivendo questa storia: ${parente ? `la voce di ${parente}` : 'la tua voce'} è al sicuro, il testo arriva tra poco.`)
            : el('div', {}, ...(m.text ?? '').split(/\n+/).filter(Boolean).map((p) => el('p', {}, p))),
        ),
        areaCorrezioni,
      ),
    ),
  );

  // Correzioni: scrivendo (modifica diretta) o parlando (Claude applica solo
  // ciò che chiede — la voce originale non si tocca mai).
  function mostraPulsanti(): void {
    const fotoInput = el('input', { type: 'file', accept: 'image/*', multiple: '', hidden: '' }) as HTMLInputElement;
    fotoInput.addEventListener('change', async () => {
      const files = Array.from(fotoInput.files ?? []).slice(0, 6);
      if (files.length === 0) return;
      areaCorrezioni.replaceChildren(el('p', { class: 'stato-ascolto' }, 'Sto caricando le foto…'));
      try {
        for (const file of files) {
          const form = new FormData();
          form.append('file', file);
          const res = await fetch(`/api/memories/${m.id}/media`, { method: 'POST', body: form });
          if (!res.ok) throw new Error(String(res.status));
        }
        void renderStoria(String(m.id));
      } catch {
        mostraPulsanti();
        areaCorrezioni.prepend(el('p', { class: 'avviso' }, 'Non sono riuscita a caricare le foto: riprova.'));
      }
    });
    const puls: Array<Node> = [];
    if (m.text && !m.trascrizioneInCorso) {
      puls.push(
        el('button', { class: 'btn-secondario', click: apriModifica }, '✏️  Correggi scrivendo'),
        el('button', { class: 'btn-secondario', click: () => void correggiAVoce() }, '🎙️  Correggi a voce'),
      );
    }
    puls.push(
      el('button', { class: 'btn-secondario', click: () => { location.hash = `#chiedi?storia=${m.id}`; } }, '💬  Qualcosa non va? Dillo al quaderno'),
      el('button', { class: 'btn-secondario', click: () => fotoInput.click() }, '📷  Aggiungi una foto'),
    );
    if (m.aggiungiVideo) {
      puls.push(el('button', { class: 'btn-secondario', 'data-ruolo': 'aggiungi-video', click: () => video.scegli() }, '🎞  Aggiungi un video'));
    }
    if (m.vetrina) {
      puls.push(
        m.vetrina.inVetrina
          ? el('div', { class: 'vetrina-stato', 'data-ruolo': 'vetrina' },
              el('p', { class: 'quando' }, '👪  La possono leggere anche i parenti'),
              el('button', { class: 'btn-quieto', click: () => void cambiaVetrina(false) }, 'Non farla più leggere ai parenti'),
            )
          : el('button', { class: 'btn-quieto', 'data-ruolo': 'vetrina', click: chiediVetrina }, '👪  Falla leggere ai parenti'),
      );
    }
    if (m.reactions.length === 0) {
      puls.push(el('button', { class: 'btn-quieto', click: () => apriRisposta(m.id, areaCorrezioni, { onDone: () => void renderStoria(String(m.id)), onCancel: mostraPulsanti }) }, `💌  Un messaggio per ${caro}`));
    }
    puls.push(
      el('button', { class: 'btn-quieto btn-pericolo azione-separata', click: chiediEliminaStoria }, '🗑  Non tenere questa storia'),
      fotoInput,
    );
    areaCorrezioni.replaceChildren(...puls);
  }
  // Ha il filo sotto gli occhi: le reazioni di Vincenzo su questa storia sono viste. E se la
  // storia l'ha portata un parente, è vista anche lei.
  //
  // Prima si mandava SOLO se c'erano reazioni di lui: il racconto di una cugina non ne ha, e
  // la risposta che mamma gli lascia è sua — quindi il pallino sulla Casa restava acceso per
  // dieci giorni anche dopo che l'aveva ascoltato e le aveva risposto. Se n'è accorta lei.
  if (m.reactions.some((r) => r.author !== 'mamma') || parente) {
    api.post<{ viste: number }>(`/api/memories/${m.id}/reactions/seen`)
      .then((r) => { if (r.viste > 0) aggiornaPallino(Math.max(0, novitaNote - r.viste)); })
      .catch(() => undefined);
  }

  // Conferma AL POSTO dei pulsanti, dove ha appena toccato. La storia di un parente non è nel suo
  // libro (niente temi, niente album) e la voce non è la sua; e se il parente l'ha scritta, di voce
  // non ce n'è: restano le sue parole.
  // La vetrina per i parenti: decide lei quali sue storie possono leggere (testo e foto, mai la voce).
  function chiediVetrina(): void {
    const v = m.vetrina;
    if (!v) return;
    const chi = v.parenti.length === 0 ? 'i parenti' : v.parenti.length === 1 ? v.parenti[0]! : `${v.parenti.slice(0, -1).join(', ')} e ${v.parenti.at(-1)}`;
    areaCorrezioni.replaceChildren(
      el('div', { class: 'carta' },
        el('p', { class: 'domanda-testo' }, 'Vuoi far leggere questa storia ai parenti?'),
        el('p', {}, `La potranno leggere ${chi} dalla loro pagina, e chi apre il loro link: il testo e le foto, mai la tua voce. Puoi toglierla quando vuoi.`),
        el('button', { class: 'btn-verde', click: () => void cambiaVetrina(true) }, 'Sì, falla leggere'),
        el('button', { class: 'btn-quieto', click: mostraPulsanti }, 'No, lasciamo stare'),
      ),
    );
    areaCorrezioni.scrollIntoView({ block: 'center' });
  }
  async function cambiaVetrina(si: boolean): Promise<void> {
    try {
      const r = await api.put<{ vetrina: typeof m.vetrina }>(`/api/memories/${m.id}/vetrina`, { inVetrina: si });
      m.vetrina = r.vetrina;
      mostraPulsanti();
      areaCorrezioni.prepend(el('p', { class: 'esito', role: 'status' }, si ? 'Fatto: ora la possono leggere anche i parenti.' : 'Fatto: i parenti non la vedono più.'));
    } catch (err) {
      mostraPulsanti();
      const piena = err instanceof ApiError && err.status === 409;
      areaCorrezioni.prepend(el('p', { class: 'avviso' }, piena
        ? `Ai parenti ne fai già leggere ${m.vetrina?.max ?? 40}: per aggiungerne una, prima togline un'altra.`
        : 'Non ci sono riuscita: riprova.'));
    }
  }
  function chiediEliminaStoria(): void {
    const haVoce = audio.length > 0 || m.registrazioneNascosta === true;
    areaCorrezioni.replaceChildren(
      el('div', { class: 'carta' },
        el('p', { class: 'domanda-testo' }, parente ? `Vuoi togliere la storia di ${parente} dal tuo quaderno?` : 'Vuoi togliere questa storia dal tuo libro?'),
        el('p', {}, !parente ? `La tua voce e il testo non vanno persi: ${caro} potrà sempre recuperarli.`
          : haVoce ? `La voce di ${parente} e il testo non vanno persi: ${caro} potrà sempre recuperarli.`
          : `Le parole di ${parente} non vanno perse: ${caro} potrà sempre recuperarle.`),
        el('button', { class: 'btn-pericolo', click: async () => {
          try {
            await api.post(`/api/memories/${m.id}/hide`);
            location.hash = '#home';
          } catch {
            mostraPulsanti();
            areaCorrezioni.prepend(el('p', { class: 'avviso' }, 'Non sono riuscita a toglierla: riprova.'));
          }
        } }, 'Sì, toglila'),
        el('button', { class: 'btn-quieto', click: mostraPulsanti }, 'No, tienila'),
      ),
    );
    areaCorrezioni.scrollIntoView({ block: 'center' });
  }
  function apriModifica(): void {
    const titoloIn = el('input', { class: 'scrivi-testo', value: m.title, 'aria-label': 'Titolo' }) as HTMLInputElement;
    const testoTa = el('textarea', { class: 'scrivi-testo grande', rows: '10', 'aria-label': 'Testo del racconto' }) as HTMLTextAreaElement;
    testoTa.value = m.text ?? '';
    const salva = el('button', { class: 'btn-verde' }, 'Salva le modifiche ✓') as HTMLButtonElement;
    const esito = el('p', { role: 'status' }, '');
    salva.addEventListener('click', async () => {
      salva.disabled = true;
      try {
        await api.put(`/api/memories/${m.id}`, { title: titoloIn.value.trim(), text: testoTa.value.trim() });
        void renderStoria(String(m.id));
      } catch {
        esito.className = 'avviso';
        esito.textContent = 'Non riesco a salvare adesso: riprova tra un momento.';
        salva.disabled = false;
      }
    });
    areaCorrezioni.replaceChildren(
      el('div', { class: 'scrivi-area' }, titoloIn, testoTa, salva, esito),
      el('button', { class: 'btn-quieto', click: mostraPulsanti }, 'Annulla'),
    );
    titoloIn.focus();
  }
  async function correggiAVoce(): Promise<void> {
    const stato = el('p', { class: 'stato-ascolto', role: 'status' }, 'Un momento…');
    const fine = el('button', { class: 'btn-verde' }, 'Ho finito ✓') as HTMLButtonElement;
    const annulla = el('button', { class: 'btn-quieto' }, 'Annulla') as HTMLButtonElement;
    areaCorrezioni.replaceChildren(
      el('div', { class: 'carta centrato' },
        el('p', { class: 'domanda-testo' }, 'Dimmi cosa correggere'),
        el('p', {}, 'Per esempio: «il nome giusto è Pina, non Lina», oppure «il matrimonio era nel 1954».'),
        stato, fine, annulla),
    );
    const rec = new Recorder({ onError: (msg) => { stato.textContent = msg; } });
    try {
      await rec.start({ purpose: 'turn' });
      stato.textContent = 'Ti ascolto…';
    } catch {
      stato.textContent = 'Non riesco a usare il microfono: prova con «Correggi scrivendo».';
      fine.hidden = true;
      annulla.addEventListener('click', mostraPulsanti);
      return;
    }
    const spegni = (): void => { void rec.stop().catch(() => undefined); };
    onLeaveScreen = spegni;
    const lascia = (): void => { if (onLeaveScreen === spegni) onLeaveScreen = null; };
    annulla.addEventListener('click', async () => {
      lascia();
      try { await rec.stop(); } catch { /* ok */ }
      mostraPulsanti();
    });
    fine.addEventListener('click', async () => {
      fine.disabled = true;
      lascia();
      stato.textContent = 'Applico la tua correzione…';
      try {
        const { recordingId, durationS, chunkCount } = await rec.stop();
        const { mediaId } = await finishRecording<{ mediaId: number }>(recordingId, {
          durationS, expectedChunks: chunkCount,
        });
        void deleteRecording(recordingId);
        await api.post(`/api/memories/${m.id}/correct`, { mediaId });
        void renderStoria(String(m.id));
      } catch {
        stato.textContent = 'Non sono riuscita ad applicare la correzione: la tua voce è salvata, riprova.';
        fine.disabled = false;
      }
    });
  }
  mostraPulsanti();
  // Arrivata da «I tuoi video»: la pagina si apre all'altezza del video (che non parte da solo).
  const daGuardare = Number(params?.get('video'));
  if (Number.isSafeInteger(daGuardare) && daGuardare > 0) scorriAlVideo(daGuardare);
}

async function renderFamiglia(): Promise<void> {
  const persone = await api.get<Array<{ id: number; display_name: string; relation_to_mamma: string | null; photo_media_id: number | null; mentions: number }>>('/api/people');
  const search = el('input', { class: 'scrivi-testo mb-4', type: 'search', placeholder: 'Cerca una persona…', 'aria-label': 'Cerca una persona' });
  const grid = el('div', { class: 'griglia-volti' });
  const empty = el('p', { class: 'vuoto', role: 'status', hidden: '' }, 'Non trovo questo nome. Prova con il nome o il legame di famiglia.');
  const cards = persone.map((p) => {
    const card = el('button', { class: 'volto', click: () => { location.hash = `#persona/${p.id}`; } },
      avatar(p.display_name, p.photo_media_id), el('div', { class: 'person-copy' },
        el('span', {}, p.display_name),
        p.relation_to_mamma ? el('span', { class: 'relazione' }, p.relation_to_mamma) : null,
      ),
    );
    grid.append(card);
    return { card, text: `${p.display_name} ${p.relation_to_mamma ?? ''}`.toLocaleLowerCase('it') };
  });
  search.addEventListener('input', () => {
    const query = search.value.trim().toLocaleLowerCase('it');
    for (const { card, text } of cards) card.hidden = !text.includes(query);
    empty.hidden = cards.some(({ card }) => !card.hidden);
  });
  screen(
    testata('La famiglia'),
    el('section', { class: 'family-intro', 'aria-label': 'Le persone dei tuoi racconti' },
      el('p', {}, 'I volti, i legami, le storie che ci uniscono.'),
      el('button', { class: 'btn-secondario', click: () => { location.hash = '#albero'; } }, icon('tree'), ' Vedi l’albero di famiglia'),
    ),
    el('div', { class: 'correction-actions family-actions' },
      el('a', { class: 'btn-secondario', href: '#aggiungi-persona' }, 'Aggiungi una persona'),
      el('a', { class: 'btn-quieto', href: '#persone-rimosse' }, 'Persone rimosse')),
    el('p', { class: 'field-help' }, 'Apri una persona e scegli «Modifica la persona» per correggere nome, sottotitolo o gruppo nell’albero.'),
    persone.length === 0
      ? el('div', { class: 'vuoto' },
          el('p', {}, 'Man mano che racconti, le persone della famiglia compariranno qui, con le loro storie.'),
          el('button', { class: 'btn-primario', click: () => { location.hash = '#scegli'; } }, 'Racconta una persona cara'),
        )
      : el('div', {}, search, grid, empty),
  );
}

async function renderPersona(id: string): Promise<void> {
  const p = await api.get<{
    displayName: string; relation: string | null; birth: string | null; photoMediaId: number | null;
    narrative: string | null;
    pageStatus: PageStatus;
    facts: Array<{ fact_type: string; value_text: string | null }>;
    memories: MemoryCard[];
  }>(`/api/people/${id}`);
  // «Sistemala tu»: nome, relazione, data di nascita, doppioni e via dall'albero —
  // tutto correggibile da mamma, con le conferme AL POSTO dei pulsanti.
  const attrezzi = el('div', { class: 'pila my-2' });
  const esitoP = el('p', { class: 'esito centrato', role: 'status' }, '');
  const mostraAttrezzi = (): void => {
    attrezzi.replaceChildren(el('button', { class: 'btn-secondario', click: async () => apriStessaPersona() }, 'È la stessa persona di…'));
  };
  async function apriStessaPersona(): Promise<void> {
    attrezzi.replaceChildren(el('p', { class: 'didascalia' }, 'Un momento…'));
    const g = await api.get<FamilyGraph>('/api/family/graph').catch(() => null);
    const altri = (g?.nodes ?? []).filter((n) => n.status === 'confirmed' && !n.isMamma && n.id !== Number(id));
    if (altri.length === 0) { mostraAttrezzi(); return; }
    const norm = (t: string) => t.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const cerca = el('input', { class: 'scrivi-testo', placeholder: 'Cerca il nome…', 'aria-label': 'Cerca una persona' }) as HTMLInputElement;
    const lista = el('div', { class: 'selettore-lista' });
    const riga = (n: { id: number; name: string; relation: string | null }): HTMLElement =>
      el('button', { class: 'storia-riga', click: () => {
        attrezzi.replaceChildren(
          el('p', { class: 'didascalia centrato' },
            `«${p.displayName}» e «${n.name}» sono la stessa persona? Terrò «${n.name}», e "${p.displayName}" resterà come soprannome.`),
          el('button', { class: 'btn-verde', click: async () => {
            try {
              await api.post(`/api/people/${id}/stessa`, { altroId: n.id });
              location.hash = `#persona/${n.id}`;
            } catch { esitoP.textContent = 'Non ci sono riuscita: riprova.'; mostraAttrezzi(); }
          } }, 'Sì, è la stessa: uniscile'),
          el('button', { class: 'btn-quieto', click: mostraAttrezzi }, 'No, lascia stare'),
        );
      } }, el('div', {}, el('div', {}, n.name), n.relation ? el('div', { class: 'quando' }, n.relation) : null));
    const aggiorna = (): void => {
      const q = norm(cerca.value.trim());
      const filtrate = q ? altri.filter((n) => norm(n.name).includes(q)) : altri;
      lista.replaceChildren(...filtrate.slice(0, 30).map(riga));
    };
    cerca.addEventListener('input', aggiorna);
    aggiorna();
    attrezzi.replaceChildren(
      el('p', { class: 'didascalia' }, 'Di chi è un doppione questa scheda?'),
      cerca,
      el('button', { class: 'btn-quieto', click: mostraAttrezzi }, 'Annulla'),
      lista,
    );
  }
  mostraAttrezzi();
  screen(
    testata(p.displayName),
    el('div', { class: 'correction-actions' },
      el('a', { class: 'btn-secondario', href: `#modifica-persona/${id}` }, 'Modifica la persona'),
      el('a', { class: 'btn-quieto', href: `#albero?persona=${id}` }, 'Vedi nell’albero')),
    el('div', { class: 'ritratto-persona' }, avatar(p.displayName, p.photoMediaId)),
    p.relation ? el('p', { class: 'data-oggi centrato' }, p.relation) : null,
    p.birth ? el('p', { class: 'data-oggi centrato' }, `🎂 ${fmtDate(p.birth)}`) : null,
    pageEditor('person', id, p.narrative, p.pageStatus, () => renderPersona(id)),
    p.narrative
      ? el('div', { class: 'pagina-narrativa' },
          ...p.narrative.split(/\n+/).filter(Boolean).map((par) => el('p', {}, par)))
      : null,
    p.facts.length > 0
      ? el('div', { class: 'carta' },
          el('p', { class: 'etichetta' }, 'Quello che mi hai raccontato'),
          ...p.facts.map((f) => el('div', { class: 'fatto' }, f.value_text ?? '')),
        )
      : null,
    el('h2', { class: 'sezione' }, p.memories.length > 0 ? `Nei tuoi ricordi (${p.memories.length})` : 'Ancora nessuna storia'),
    ...p.memories.map((m) =>
      el('button', { class: 'storia-riga', click: () => { location.hash = `#storia/${m.id}`; } },
        el('div', {},
          el('div', {}, m.title),
          el('div', { class: 'quando' }, `raccontato il ${fmtDate(m.toldAt)}`),
        ),
      ),
    ),
    el('details', { class: 'carta gestione-persona mt-6' },
      el('summary', {}, 'Unisci una scheda doppia'), attrezzi, esitoP,
    ),
  );
}

async function renderLibro(): Promise<void> {
  const libro = await api.get<{ title: string; pages: number; stories?: { total: number; yours: number; family: number }; chapters: Array<{ slug: string; title: string; count: number }>; dedication?: BookDedication | null }>('/api/book');
  const total = libro.stories?.total ?? libro.pages;
  screen(
    testata('Il tuo libro'),
    el('div', { class: `book-workspace${libro.dedication ? ' has-dedication' : ''}` },
      el('section', { class: 'book-cover', 'data-surprise-anchor': 'libro', 'aria-label': 'Copertina del tuo libro' },
        el('p', { class: 'eyebrow' }, 'MEMORIE DI FAMIGLIA'),
        icon('book'),
        el('h2', {}, libro.title),
        el('p', {}, `Nel quaderno: ${total} ricord${total === 1 ? 'o' : 'i'}`),
        libro.stories ? el('p', { class: 'book-story-counts' },
          `${libro.stories.yours} ${libro.stories.yours === 1 ? 'tuo ricordo' : 'tuoi ricordi'}${libro.stories.family ? ` e ${libro.stories.family} ${libro.stories.family === 1 ? 'ricordo dei tuoi cari' : 'ricordi dei tuoi cari'}` : ''}.`,
        ) : null,
        libro.stories?.family ? el('a', { class: 'btn-secondario', href: '#voci' }, 'Leggi i ricordi dei tuoi cari') : null,
        // Il PDF degli export, col cookie di famiglia: si apre in una scheda nuova, il quaderno resta qui.
        el('a', { class: 'btn-secondario mt-3', href: '/api/book/pdf', target: '_blank', rel: 'noopener' }, 'Scarica il libro'),
      ),
      el('section', { class: 'book-index', 'aria-labelledby': 'book-index-title' },
        libro.dedication ? el('section', { class: 'book-dedication', 'aria-labelledby': 'book-dedication-title' },
          el('p', { class: 'eyebrow' }, `${libro.dedication.from} · per ${libro.dedication.to}`),
          el('h2', { id: 'book-dedication-title' }, libro.dedication.title),
          el('blockquote', {}, libro.dedication.text)) : null,
        el('h2', { class: 'sezione', id: 'book-index-title' }, 'I capitoli'),
        libro.chapters.length ? el('p', { class: 'data-oggi' },
          `${libro.chapters.length} ${libro.chapters.length === 1 ? 'capitolo per argomento' : 'capitoli per argomento'}. Una storia può comparire in più capitoli.`,
        ) : null,
        libro.chapters.length === 0
          ? el('p', { class: 'vuoto' }, 'Il tuo libro è appena nato: la prima storia sarà la prima pagina.')
          : el('div', {},
              ...libro.chapters.map((c, i) =>
                el('button', { class: 'storia-riga', click: () => { location.hash = `#leggi/${c.slug}`; } },
                  el('span', { class: 'chapter-number', 'aria-hidden': 'true' }, String(i + 1).padStart(2, '0')),
                  el('div', {}, el('div', {}, c.title), el('div', { class: 'quando' }, `${c.count} ricord${c.count === 1 ? 'o' : 'i'}`)),
                ),
              ),
            ),
      ),
    ),
  );
}

/* ---------- impostazioni di mamma ---------- */

async function renderImpostazioni(): Promise<void> {
  const s = await api.get<HomeState>('/api/state');
  replyVoice = s.replyVoice;
  setCaro(s.caroName);
  applicaOrnamenti(s.ornamenti);

  const statoEl = el('p', { role: 'status', class: 'esito' }, '');

  // Dimensione del testo: tre scelte, applicate all'istante (l'anteprima è la classe).
  const scelte = [
    { v: '1', label: 'Normale', cls: '' },
    { v: '1.15', label: 'Grande', cls: ' grande' },
    { v: '1.3', label: 'Molto grande', cls: ' molto-grande' },
  ];
  const bottoniScala = scelte.map((sc) =>
    el('button', {
      class: `scelta-testo${sc.cls}`,
      click: () => {
        try { localStorage.setItem(FONT_KEY, sc.v); } catch { /* modalità privata */ }
        applicaScalaTesto(sc.v);
        aggiornaScala();
        statoEl.textContent = 'Ecco, il testo ora è così ✓';
      },
    }, 'Aa  ', sc.label) as HTMLButtonElement,
  );
  function aggiornaScala(): void {
    const cur = scalaTesto();
    scelte.forEach((sc, i) => bottoniScala[i]!.classList.toggle('attiva', sc.v === cur));
  }
  aggiornaScala();

  // Voce delle risposte: impostazione globale, la stessa che vede Vincenzo.
  const btnConVoce = el('button', { class: 'scelta-testo' }, '🔊  Con la voce') as HTMLButtonElement;
  const btnSenzaVoce = el('button', { class: 'scelta-testo' }, '🔇  Senza voce') as HTMLButtonElement;
  function aggiornaVoce(): void {
    btnConVoce.classList.toggle('attiva', replyVoice);
    btnSenzaVoce.classList.toggle('attiva', !replyVoice);
  }
  async function impostaVoce(v: boolean): Promise<void> {
    try {
      const res = await api.put<{ replyVoice: boolean }>('/api/settings', { replyVoice: v });
      replyVoice = res.replyVoice;
      aggiornaVoce();
      statoEl.textContent = v ? 'D’accordo: ti leggerò le risposte ad alta voce ✓' : 'Va bene: risposte solo scritte ✓';
    } catch {
      statoEl.textContent = 'Non riesco a salvare in questo momento: riprova tra poco.';
    }
  }
  btnConVoce.addEventListener('click', () => void impostaVoce(true));
  btnSenzaVoce.addEventListener('click', () => void impostaVoce(false));
  aggiornaVoce();

  // La pagina iniziale: card facoltative accendibili e spegnibili (vale su tutti i dispositivi).
  const homeToggle = (chiave: 'homeDomanda' | 'homeAccadde' | 'homeBaule', attuale: boolean, label: string): HTMLElement => {
    const btn = el('button', { class: 'scelta-testo' + (attuale ? ' attiva' : ''), 'aria-pressed': String(attuale) }, label) as HTMLButtonElement;
    btn.addEventListener('click', async () => {
      try {
        await api.put('/api/settings', { [chiave]: !attuale });
        statoEl.textContent = !attuale ? `D'accordo, «${label}» tornerà sulla pagina iniziale ✓` : `Va bene, «${label}» non comparirà più ✓`;
        render();
      } catch {
        statoEl.textContent = 'Non riesco a salvare in questo momento: riprova tra poco.';
      }
    });
    return btn;
  };

  // Come si vede il quaderno. Il quaderno è un album e resta un album: quello che si sceglie
  // qui è quanta DECORAZIONE portare addosso — la grana della carta, i caratteri da libro, gli
  // angolini delle fotografie. Niente si sposta: i pulsanti restano dove sono sempre stati.
  //
  // È la sua via d'uscita delle nove di sera, quando non ci vede bene e non vuole telefonare a
  // nessuno. Non è una preferenza del dispositivo: sta sul server, così anche Vincenzo può
  // alleggerirla da lontano se lei non riesce più a leggere.
  const bottoniVeste: Array<[boolean, HTMLButtonElement]> = [];
  const aggiornaVeste = (): void => {
    for (const [ridotti, btn] of bottoniVeste) {
      const attiva = (ornamenti() === 'ridotti') === ridotti;
      btn.classList.toggle('attiva', attiva);
      btn.setAttribute('aria-pressed', String(attiva));
    }
  };
  const scegliVeste = (ridotti: boolean, label: string, spiega: string): HTMLElement => {
    const btn = el('button', { class: 'scelta-veste' },
      el('strong', {}, label), el('small', {}, spiega)) as HTMLButtonElement;
    bottoniVeste.push([ridotti, btn]);
    btn.addEventListener('click', async () => {
      if ((ornamenti() === 'ridotti') === ridotti) return;
      try {
        const esito = await api.put<{ ornamenti: Ornamenti }>('/api/ornamenti', { ridotti });
        applicaOrnamenti(esito.ornamenti);
        // Si aggiornano i due pulsanti, non tutta la schermata: un `render()` qui cancellerebbe
        // il messaggio appena scritto e mamma non saprebbe se ha funzionato.
        aggiornaVeste();
        statoEl.textContent = esito.ornamenti === 'ridotti'
          ? 'Ecco fatto: adesso è tutto più semplice da leggere ✓'
          : 'Va bene, il quaderno è tornato con la sua carta ✓';
      } catch {
        statoEl.textContent = 'Non riesco a salvare in questo momento: riprova tra poco.';
      }
    });
    return btn;
  };

  const cartaVeste = el('div', { class: 'carta carta-veste' },
    el('p', { class: 'etichetta' }, 'Come si vede il quaderno'),
    el('p', {}, 'Puoi cambiare idea quando vuoi: le tue storie, le tue foto e i pulsanti restano tutti al loro posto.'),
    el('div', { class: 'pila-scelte' },
      scegliVeste(false, 'Con la sua carta', 'La carta del quaderno, i caratteri da libro e le fotografie montate coi loro angolini.'),
      scegliVeste(true, 'Più semplice da leggere', 'Carta liscia e caratteri più netti. Tutto resta dov\u2019è: cambia solo la decorazione.'),
    ),
  );
  aggiornaVeste();

  screen(
    testata('Impostazioni'),
    el('div', { class: 'carta', 'data-surprise-anchor': 'impostazioni' },
      el('p', { class: 'etichetta' }, 'Dimensione del testo'),
      el('div', { class: 'pila-scelte' }, ...bottoniScala),
    ),
    cartaVeste,
    el('div', { class: 'carta' },
      el('p', { class: 'etichetta' }, 'La tua pagina iniziale'),
      el('p', {}, 'Scegli cosa vuoi trovare sulla pagina iniziale (tocca per accendere o spegnere):'),
      el('div', { class: 'pila-scelte' },
        homeToggle('homeDomanda', s.home?.domanda ?? true, 'La domanda di oggi'),
        homeToggle('homeAccadde', s.home?.accadde ?? true, '🗓 Accadde oggi'),
        homeToggle('homeBaule', s.home?.baule ?? true, '🧳 Dal baule dei ricordi'),
      ),
    ),
    el('div', { class: 'carta' },
      el('p', { class: 'etichetta' }, 'La mia voce'),
      el('p', {}, 'Vuoi che ti legga le risposte ad alta voce?'),
      el('div', { class: 'pila-scelte' }, btnConVoce, btnSenzaVoce),
      el('button', {
        class: 'btn-secondario mt-3',
        click: () => { void speak(`Ciao ${s.name}! Io parlo così quando ti rispondo.`, { force: true }); },
      }, '🔊  Prova la voce'),
      el('p', { class: 'data-oggi mt-3' }, 'Per alzare o abbassare il volume usa i tasti sul bordo del tablet.'),
    ),
    // Da Safari gli avvisi non arrivano: prima di tutto, l'icona sulla schermata Home.
    appInstallata() ? null : el('div', { class: 'carta' },
      el('p', { class: 'etichetta' }, '📲 Aggiungi Memorie alla schermata Home'),
      el('ol', {},
        el('li', {}, 'In Safari tocca il pulsante Condividi (il quadrato con la freccia in alto).'),
        el('li', {}, 'Scegli «Aggiungi alla schermata Home».'),
        el('li', {}, 'Tocca «Aggiungi»: da ora apri Memorie dall’icona, come le altre app.'),
      ),
      el('p', {}, `Così Memorie si apre a tutto schermo e può avvisarti quando ${caro} ti scrive.`),
    ),
    cartaAvvisi(statoEl),
    statoEl,
    el('button', { class: 'btn-quieto', click: () => { location.hash = '#aiuto'; } }, '🛟 Come si usa l’app'),
  );
}

/* ---------- aiuto: come si usa ---------- */

function renderAiuto(): void {
  const passi: Array<[string, string, string]> = [
    ['🎙️', 'Racconta', `Dalla pagina iniziale tocca il pulsante rosso e parla con calma, come se raccontassi a ${caro}. Quando hai finito, tocca «Ho finito».`],
    ['📖', 'Riascolta e rileggi', 'In «I tuoi ricordi» trovi tutte le tue storie, scritte e con la tua voce. Tocca una storia per rileggerla o riascoltarla.'],
    ['🖼️', 'Le fotografie', 'Tocca una foto e poi «Raccontami questa foto»: racconta chi c’è e che giorno era, e la storia resterà attaccata alla foto.'],
    ['💬', 'Gli spunti', 'Se non sai da dove cominciare, negli «Spunti di conversazione» ci sono domande pensate per te.'],
    ['💬', 'Parla col tuo quaderno', `Se una data, una foto o un nome non vanno, diglielo a voce o per iscritto: sistema subito le piccole cose e puoi sempre annullare. Le cose grandi le passa a ${caro}.`],
    ['💙', `${caro} c’è`, `Se qualcosa non funziona, non preoccuparti: niente va mai perso. Chiama ${caro} e sistemate tutto insieme.`],
  ];
  const testoCompleto = passi.map(([, t, d]) => `${t}. ${d}`).join('\n');
  screen(
    testata('Come si usa'),
    ...passi.map(([icona, titolo, testo]) =>
      el('div', { class: 'carta passo-aiuto' },
        el('span', { class: 'icona', 'aria-hidden': 'true' }, icona),
        el('div', {},
          el('p', { class: 'domanda-testo mb-2' }, titolo),
          el('p', { class: 'm-0' }, testo),
        ),
      ),
    ),
    el('button', { class: 'btn-secondario', click: () => void speak(testoCompleto, { force: true }) }, '🔊  Leggimela ad alta voce'),
  );
}

/* ---------- router ---------- */

let navigation = 0;
function render(): void {
  clearSurprises();
  const currentNavigation = ++navigation;
  window.clearTimeout(screenLoadTimer);
  cancelPendingReads();
  stopSpeaking();
  const leave = onLeaveScreen;
  onLeaveScreen = null;
  leave?.();
  const hash = location.hash || '#home';
  const [route, queryStr] = hash.slice(1).split('?');
  const params = new URLSearchParams(queryStr ?? '');
  const parts = (route ?? 'home').split('/');
  app.dataset['schermata'] = parts[0] ?? 'home';
  aggiornaBarra(parts[0] ?? 'home');
  const loading = window.setTimeout(() => {
    if (navigation !== currentNavigation) return;
    app.setAttribute('aria-busy', 'true');
    app.replaceChildren(el('p', { class: 'caricamento', role: 'status' }, 'Apro il quaderno…'));
  }, 250);
  screenLoadTimer = loading;

  const go = async (): Promise<void> => {
    // «Scegli il ritratto dalle tue foto» vale solo nella galleria: altrove un dialogo foto
    // (da una storia) non deve proporre il ritratto di qualcuno.
    const perRitratto = Number(params.get('ritratto'));
    ritrattoPer = parts[0] === 'foto' && Number.isSafeInteger(perRitratto) && perRitratto > 0
      ? { id: perRitratto, name: params.get('nome')?.slice(0, 80) || 'questa persona' } : null;
    switch (parts[0]) {
      case 'home': return renderHome();
      case 'luoghi': return renderHeartPlaces({ screen, testata, onLeave: (cleanup) => { onLeaveScreen = cleanup; } }, params);
      case 'scegli': return renderScegli();
      case 'registra': {
        const q = params.get('q');
        const questionId = q ? Number(q) : null;
        const f = params.get('foto');
        const fotos = (params.get('fotos') ?? '').split(',').map(Number).filter((n) => Number.isInteger(n) && n > 0);
        if (params.get('mode') === 'telefonata') return renderTelefonata();
        return params.get('mode') === 'dialogo'
          ? renderDialogo(questionId, fotos, params.get('ricetta') === '1')
          : renderAscolto(questionId, f ? Number(f) : null);
      }
      case 'ricordi': return renderArchive({ screen, testata }, params.get('q') ?? '');
      case 'menu': return screen(testata('Il menu del quaderno'), notebookMenu());
      case 'tema': return renderTema(parts[1] ?? '');
      case 'storia': return renderStoria(parts[1] ?? '', params);
      case 'famiglia': return renderFamiglia();
      case 'albero': return renderFamilyTree({ screen, testata, onLeave: (cleanup) => { onLeaveScreen = cleanup; } }, params);
      case 'identita-famiglia': return renderFamilyIdentity({ screen, testata });
      case 'cronologia': return renderCronologia();
      case 'voci': return renderVoci();
      // «Novità» e «Posta» sono la stessa porta: #novita resta valido (è nelle notifiche e nelle email).
      case 'novita':
      case 'posta': return renderPosta({ screen, testata, speak, caro, apriRisposta, esitoRisposta, daVedere: sezioneDaVedere });
      case 'ordine': return renderOrdine();
      case 'cerca': return renderSearch({ screen, testata }, params.get('q') ?? '');
      case 'ricette': return renderRicette();
      case 'chiedi': return renderQuaderno({ screen, testata, onLeave: (cleanup) => { onLeaveScreen = cleanup; }, speak, caro, aggiornaNovita: () => void controllaNovita() }, params);
      // Gli indirizzi di prima restano validi: con l'album acceso «#leggi/…» apre lo stesso
      // capitolo sfogliabile, così le notifiche e le lettere già mandate continuano a portare
      // dove devono. Spento, è il capitolo di sempre.
      // `#leggi/:slug` resta un indirizzo valido per sempre — è nelle lettere già partite e
      // nelle scorciatoie della PWA installata — e apre il capitolo sfogliabile.
      case 'leggi': return renderAlbum({ screen, testata, speak }, 'capitolo', parts[1] ?? '', 0);
      case 'album': return renderAlbum({ screen, testata, speak }, parts[1] ?? '', parts[2] ?? '', Number(parts[3] ?? 0));
      case 'spunti': return renderSpunti();
      case 'foto': return renderFoto();
      case 'persona': return renderPersona(parts[1] ?? '');
      case 'modifica-persona': return renderPersonEditor({ screen, testata }, parts[1] ?? '');
      case 'aggiungi-persona': return renderPersonEditor({ screen, testata });
      case 'persone-rimosse': return renderRemovedPeople({ screen, testata });
      case 'libro': return renderLibro();
      case 'impostazioni': return renderImpostazioni();
      case 'aiuto': return renderAiuto();
      default: return renderHome();
    }
  };
  void go().catch((error: unknown) => {
    if (navigation === currentNavigation && !(error instanceof DOMException && error.name === 'AbortError')) schermataErrore(error);
  }).finally(() => {
    window.clearTimeout(loading);
    if (navigation === currentNavigation) app.removeAttribute('aria-busy');
  });
}

window.addEventListener('hashchange', () => {
  ricordaIlPosto();                     // dove stava, prima di lasciare questa schermata
  const st = history.state as { mIdx?: number } | null;
  if (st?.mIdx === undefined) {
    // Voce nuova (navigazione in avanti dell'app): la timbriamo.
    navIdx += 1;
    history.replaceState({ mIdx: navIdx }, '');
    postoDaRiprendere = null;           // una pagina nuova si apre dall'inizio
  } else {
    // Voce già timbrata: gesto indietro/avanti del browser.
    navIdx = st.mIdx;
    postoDaRiprendere = posti.get(navIdx) ?? null;
  }
  render();
});
void bootAuth().then(() => {
  stampHistory();
  render();
});
